import type { inferRouterOutputs } from "@trpc/server";
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ChevronDown,
  ChevronUp,
  Clock,
  Columns3,
  Filter,
  FolderOpen,
  Plus,
  Rows3,
  SlidersHorizontal,
  Trash2,
  TrendingUp,
  Wrench,
  X,
} from "lucide-react";
import { memo, useEffect, useMemo, useState, type ReactNode } from "react";
import { VehicleHistoryModal } from "../components/domain/VehicleHistoryModal.js";
import { VEHICLE_STATUS_STYLE } from "../lib/vehicle-status.js";
import { Link, useLocation } from "wouter";
import { BulkActionBar, BulkGroup } from "../components/common/BulkActionBar.js";
import { CarLogo } from "../components/common/CarLogo.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FILTER_PANEL_STYLE,
  FilterField,
} from "../components/common/FilterBar.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { BeneficeCell, computeBenefice } from "../components/domain/BeneficeCell.js";
import { CommissionCascade } from "../components/domain/CommissionCascade.js";
import { InvoiceDraftModal } from "../components/domain/InvoiceDraftModal.js";
import { DossierWizard } from "../components/dossier/DossierWizard.js";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { setPin, usePin } from "../hooks/usePin.js";
import { useBulkSelection } from "../hooks/useBulkSelection.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { useT } from "../i18n/index.js";
import { downloadCsv } from "../lib/csv.js";
import { formatMoney, toNumber } from "../lib/format-fr.js";
import { trpc, type AppRouter } from "../lib/trpc.js";
import { abrirArquivo } from "../lib/open-file.js";
import { useGrantOnly } from "../hooks/useGrantOnly.js";
import { Paginacao } from "../components/common/Paginacao.js";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type DossierRowData = RouterOutputs["dossiers"]["list"]["rows"][number];

const PAGE_SIZE = 50;

/**
 * F-dossiers-sel-totals — valor pago ao técnico de um dossier, seguindo a MESMA
 * regra do CommissionCascade: amount armazenado quando presente; senão
 * facturado × %; modelo legacy usa o valor legado. Usado nos totais da seleção
 * e no relatório PDF.


 */
/**
 * F-list-date (CEO 2026-10-02, "porque não há datas"): a coluna só mostrava a
 * data da FATURA, vazia até faturar. Cai na data do devis e, por fim, na de
 * criação; o tooltip diz qual é.
 */
function dataDaLista(
  d: DossierRowData,
): { date: string; kind: "invoice" | "repaired" | "devis" | "created" } | null {
  if (d.invoiceDate) return { date: d.invoiceDate, kind: "invoice" };
  // CEO 2026-10-18: carro reparado e ainda sem fatura → data da reparação
  // (a do devis confundia no card "a faturar").
  if (d.repairedAt)
    return { date: new Date(d.repairedAt).toISOString().slice(0, 10), kind: "repaired" };
  if (d.devisDate) return { date: String(d.devisDate).slice(0, 10), kind: "devis" };
  if (d.createdAt)
    return { date: new Date(d.createdAt).toISOString().slice(0, 10), kind: "created" };
  return null;
}

function techPaidOf(d: DossierRowData): number {
  const fat = Number(d.facturadoTotal) || 0;
  if (d.primaryLineCommissionModel === "legacy") {
    return fat > 0 ? Number(d.primaryLineTechnicianCommissionAmount) || 0 : 0;
  }
  const stored = d.technicianAmountTotal;
  const s = stored != null && stored !== "" ? Number(stored) : NaN;
  // F-cascade-montage — "Técnico" = o técnico do PDR; a montagem de OUTRO
  // técnico sai à parte (montageOtherOf). Antes somava os dois (7 081 em vez
  // de 7 060 na seleção do Cruspeyre, CEO 2026-10-01).
  const outro = Number(d.otherTechLinesAmountTotal) || 0;
  return Number.isFinite(s) ? s - outro : (fat * (Number(d.primaryLineTechnicianPctF8) || 0)) / 100;
}

/** Parte de OUTRO técnico (montador) nos dossiers selecionados. */
function montageOtherOf(d: DossierRowData): number {
  return Number(d.otherTechAmountTotal) || 0;
}

/**
 * F13.5 commit 3 — Tonalidades semânticas dos badges da listagem.
 * Mantém regra "lime = sucesso financeiro", "sky = em curso", "amber = atenção".
 */
/**
 * F-lista-carros (auditoria UX, item 5; CEO 24/10 "pode seguir") — a situação
 * do carro em UMA frase + a ação do próximo passo, calculadas num lugar só
 * (tabela do desktop e cartão do celular). Antes: dois selos ("Cliente · …"
 * / "Téc · …") que o operador precisava decodificar.
 */
interface SituacaoCarro {
  label: string;
  tone: string;
  sub: string | null;
  acao: { label: string; href: string; primario: boolean } | null;
}
function situacaoDoCarro(
  d: DossierRowData,
  t: ReturnType<typeof useT>,
  /** CEO 2026-10-29 — gerente de chantier: recebimento é do escritório. */
  semRecebimento = false,
): SituacaoCarro {
  const st = d.customerInvoiceStatus;
  const total = toNumber(d.customerInvoiceTotal);
  const recebidoCliente = toNumber(d.customerReceivedTotal) ?? 0;
  const pagoPeloCliente =
    st === "paid" || (total != null && total > 0 && recebidoCliente >= total - 0.01);
  const tp = d.technicianPayment;
  const ti = d.technicianInvoice;
  const tiTotal = ti ? (toNumber(ti.totalAmount) ?? 0) : 0;
  const tiPago = ti ? (toNumber(ti.paidTotal) ?? 0) : 0;
  const temTec = d.technicianName != null;
  // CEO 2026-10-26 — "técnico pago" = nenhuma peça COM VALOR pendente (linha ou
  // metade, de qualquer técnico do carro). Linha pendente de 0 € (metade da
  // garage) não prende o carro em "repasse pendente".
  const pendentes = d.pendingPayees ?? [];
  void tp;
  const tecPago = (temTec && pendentes.length === 0) || (tiTotal > 0 && tiPago >= tiTotal - 0.01);
  const faltaRepassar =
    pendentes.length > 0
      ? `${t("dossiers.sitPayoutMissingPre")} ${pendentes
          .map(
            (p) =>
              `${p.name} ${p.amount != null ? formatMoney(p.amount, { decimals: 2 }) : t("dossiers.sitPayoutTbd")}`,
          )
          .join(" · ")}`
      : null;
  // CEO 2026-10-26 ("pode alinhar") — "reparado" pela regra ÚNICA do quadro da
  // operação (PDR finalizado; espelho ES→BS segue o carro da ES), vinda do
  // servidor. Sem ela (fatura já emitida etc.), a data de reparo como antes.
  const reparado = d.repairState != null ? d.repairState === "reparado" : d.repairedAt != null;
  const href = (acao?: string) => `/dossiers/${d.id}${acao ? `?acao=${acao}` : ""}`;
  const abrir = { label: t("dossiers.actOpen"), href: href(), primario: false };
  const GRAY = "bg-neutral-700/40 text-neutral-300";
  const SKY = "bg-sky-500/15 text-sky-300";
  const LIME = "bg-lime/15 text-lime";
  const AMBER = "bg-amber-500/15 text-amber-300";
  if (d.deletedAt) return { label: t("dossiers.sitDeleted"), tone: GRAY, sub: null, acao: null };
  if (st === "cancelled") {
    return { label: t("dossiers.sitCancelled"), tone: GRAY, sub: null, acao: abrir };
  }
  if (pagoPeloCliente) {
    return {
      label: t("dossiers.sitReceived"),
      tone: LIME,
      sub: tecPago
        ? t("dossiers.sitTechPaid")
        : temTec
          ? (faltaRepassar ?? t("dossiers.sitPayoutPending"))
          : null,
      acao:
        tecPago || !temTec
          ? abrir
          : { label: t("dossiers.actPayout"), href: href("repassar"), primario: true },
    };
  }
  if (st === "issued") {
    return {
      label: recebidoCliente > 0 ? t("dossiers.sitPartial") : t("dossiers.sitInvoiced"),
      tone: SKY,
      sub: temTec ? null : t("dossiers.sitNoTech"),
      acao: semRecebimento
        ? abrir
        : { label: t("dossiers.actReceipt"), href: href("receber"), primario: true },
    };
  }
  if (reparado) {
    return {
      label: t("dossiers.sitRepairedToInvoice"),
      tone: LIME,
      sub: temTec ? null : t("dossiers.sitNoTech"),
      acao: { label: t("dossiers.actIssue"), href: href("faturar"), primario: true },
    };
  }
  if (temTec) return { label: t("dossiers.sitInRepair"), tone: AMBER, sub: null, acao: abrir };
  return {
    label: t("dossiers.sitDevisWaiting"),
    tone: AMBER,
    sub: null,
    acao: { label: t("dossiers.actLaunch"), href: href("tecnico"), primario: false },
  };
}

/** Chaves de ordenação client-side para colunas 2/4/8/10/12. */
type SortKey = "invoiceDate" | "plate" | "customer" | "facturado" | "benefice";
type SortDir = "asc" | "desc";

/** Lê um param de data (YYYY-MM-DD) da URL; string vazia se ausente/inválido. */
function dateParamFromUrl(key: string): string {
  if (typeof window === "undefined") return "";
  const p = new URLSearchParams(window.location.search).get(key);
  return p && /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : "";
}

export default function Dossiers() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const t = useT();
  // F-listas-cartoes — no celular a tabela vira cartões (hook no topo: nunca após um return).
  const isMobile = useIsMobile();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );

  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  // CEO 2026-10-26 — ficha do veículo aberta pela lista (🚗 N dossiers).
  const [fichaVeiculo, setFichaVeiculo] = useState<number | null>(null);
  // F-billing-company-label — a empresa ativa absorve o resíduo da cascade.
  const billingCompanyName =
    accessibleCompanies.find((c) => c.id === companyId)?.name ?? "Euro Storm";
  // Deep-link do card "carros a faturar" do Painel: /dossiers?cliente=<id>.
  // F-pin — alfinete global de CLIENTE: força o filtro até soltar.
  const pin = usePin();
  const pinnedCustomerId = pin?.kind === "customer" && pin.companyId === companyId ? pin.id : null;
  const [customerFilter, setCustomerFilter] = useState<number | null>(() => {
    const v = Number(new URLSearchParams(window.location.search).get("cliente"));
    return Number.isInteger(v) && v > 0 ? v : null;
  });
  const [technicianFilter, setTechnicianFilter] = useState<number | null>(null);
  // F-bs-proforma-filter (CEO 2026-10-28) — na BS: carros espelho de UMA
  // proforma ES → BS, para faturar uma proforma por vez.
  const [proformaRefFilter, setProformaRefFilter] = useState<string | null>(null);
  // Deep-link dos KPIs do Painel: /dossiers?status=pending abre já filtrado.
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    const p = new URLSearchParams(window.location.search).get("status");
    return ["pending", "issued", "paid", "cancelled"].includes(p ?? "") ? p! : "all";
  });
  const [q, setQ] = useState("");
  // Deep-link dos KPIs do Painel escopados por período: /dossiers?from=&to=
  // (YYYY-MM-DD) abre a lista já filtrada pelo mesmo intervalo do KPI clicado.
  const [from, setFrom] = useState(() => dateParamFromUrl("from"));
  const [to, setTo] = useState(() => dateParamFromUrl("to"));
  const [invoiceNumberFrom, setInvoiceNumberFrom] = useState("");
  const [invoiceNumberTo, setInvoiceNumberTo] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [withoutTechnician, setWithoutTechnician] = useState(false);
  // F-devis-skeletons-banner — devis do PDF "a executar" (aguardando técnico
  // assumir no portal): filtro próprio, separado do "sem técnico — revisar".
  const [devisToExecute, setDevisToExecute] = useState(false);
  // F-ops-home — deep-link do card "carros a faturar": só dossiers com linhas
  // de serviço (técnico já trabalhou), para a lista bater com o card.
  const [hasLines, setHasLines] = useState(
    () => new URLSearchParams(window.location.search).get("comLinhas") === "1",
  );
  // CEO 2026-10-12 — card "carros a faturar": só reparados (quadro da operação).
  const [onlyRepaired, setOnlyRepaired] = useState(
    () => new URLSearchParams(window.location.search).get("reparados") === "1",
  );
  // CEO 2026-10-13 — card do painel "dégarnissage não finalizado".
  const [degarnPending, setDegarnPending] = useState(
    () => new URLSearchParams(window.location.search).get("degarn") === "1",
  );
  // CEO 2026-10-12 — quadro da operação do painel: /dossiers?op=em_reparacao.
  const [opStatus, setOpStatus] = useState<
    "a_reparar" | "em_reparacao" | "reparado" | "entregue" | null
  >(() => {
    const v = new URLSearchParams(window.location.search).get("op");
    return v === "a_reparar" || v === "em_reparacao" || v === "reparado" || v === "entregue"
      ? v
      : null;
  });
  // F-dossiers-kpis-v2: filtra dossiers onde cliente pagou + tec pending.
  // Toggled pelo clique no card "Técnicos a pagar" do painel de KPIs.
  const [technicianPaymentPending, setTechnicianPaymentPending] = useState(false);
  // F-dossiers-pagtec-filter: dropdown dedicado da coluna PAG. TEC (pagamento
  // ao técnico/intermediário), separado do status da fatura do cliente.
  const [techPayFilter, setTechPayFilter] = useState<"all" | "pending" | "paid">("all");
  // F-dossiers-ux — Sort global aplicado no backend (antes da paginação).
  // Diferente do sort client-side por coluna (limitado à página atual).
  // CEO 2026-10-19: padrão = data exibida, mais recente primeiro; um toque inverte.
  const [sortBy, setSortBy] = useState<
    | "activity_desc"
    | "activity_asc"
    | "date_desc"
    | "date_asc"
    | "invoice_number_desc"
    | "invoice_number_asc"
    | "facturado_desc"
    | "facturado_asc"
  >("activity_desc");
  const [offset, setOffset] = useState(0);
  const [showWizard, setShowWizard] = useState(false);

  // F-layout-L1 (CEO 2026-09-13: "layout poluído") — KPIs compactos e painel
  // de filtros RECOLHIDO por padrão; estados lembrados por navegador.
  const [kpisExpanded, setKpisExpanded] = useState(() => {
    try {
      return localStorage.getItem("edsp.dossiers.kpisExpanded") === "1";
    } catch {
      return false;
    }
  });
  const toggleKpis = () =>
    setKpisExpanded((v) => {
      const n = !v;
      try {
        localStorage.setItem("edsp.dossiers.kpisExpanded", n ? "1" : "0");
      } catch {
        /* noop */
      }
      return n;
    });
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      return localStorage.getItem("edsp.dossiers.filtersOpen") === "1";
    } catch {
      return false;
    }
  });
  const toggleFiltersOpen = () =>
    setFiltersOpen((v) => {
      const n = !v;
      try {
        localStorage.setItem("edsp.dossiers.filtersOpen", n ? "1" : "0");
      } catch {
        /* noop */
      }
      return n;
    });
  /** Valor compacto p/ a linha de indicadores: 1,06 M€ · 692 k€ · 490 €. */
  const cm = (v: string | number | null | undefined) => {
    const n = Number(v ?? 0);
    if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(".", ",")} M€`;
    if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000).toLocaleString("fr-FR")} k€`;
    return `${formatMoney(n, { decimals: 0 })} €`;
  };

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      // CEO 2026-10-26 (modelo eDSP) — uma linha por veículo, com os cards de
      // todos os dossiers dele.
      oneRowPerVehicle: true,
      customerId: pinnedCustomerId ?? customerFilter ?? undefined,
      technicianId: technicianFilter ?? undefined,
      proformaRef: proformaRefFilter ?? undefined,
      // status fatura = SÓ o lado do cliente (customer_invoice.fiscal_status).
      customerInvoiceStatus:
        statusFilter === "all"
          ? undefined
          : (statusFilter as "pending" | "issued" | "paid" | "cancelled"),
      // Pag. téc = lado do pagamento ao técnico/intermediário (col PAG. TEC),
      // dropdown SEPARADO pra não confundir com o status da fatura do cliente.
      technicianPaymentStatus: techPayFilter === "all" ? undefined : techPayFilter,
      q: q || undefined,
      from: from || undefined,
      to: to || undefined,
      invoiceNumberFrom: invoiceNumberFrom.trim() || undefined,
      invoiceNumberTo: invoiceNumberTo.trim() || undefined,
      includeDeleted,
      withoutTechnician: withoutTechnician || undefined,
      devisToExecute: devisToExecute || undefined,
      hasLines: hasLines || undefined,
      onlyRepaired: onlyRepaired || undefined,
      degarnPending: degarnPending || undefined,
      opStatus: opStatus ?? undefined,
      technicianPaymentPending: technicianPaymentPending || undefined,
      sortBy,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      companyId,
      customerFilter,
      pinnedCustomerId,
      technicianFilter,
      proformaRefFilter,
      statusFilter,
      techPayFilter,
      q,
      from,
      to,
      invoiceNumberFrom,
      invoiceNumberTo,
      includeDeleted,
      withoutTechnician,
      devisToExecute,
      hasLines,
      onlyRepaired,
      degarnPending,
      opStatus,
      technicianPaymentPending,
      sortBy,
      offset,
    ],
  );

  const listQuery = trpc.dossiers.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const customersQuery = trpc.customers.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );
  const mirrorProformasQuery = trpc.dossiers.mirrorProformas.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );
  const mirrorProformas = mirrorProformasQuery.data?.rows ?? [];
  const techniciansQuery = trpc.technicians.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );
  // F7.1-2: alerta global de dossiers sem técnico
  const withoutTechQuery = trpc.dossiers.countWithoutTechnician.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );
  const withoutTechCount = withoutTechQuery.data?.count ?? 0;
  // F-devis-skeletons-banner — devis do PDF aguardando o técnico no portal
  // (estado esperado, banner informativo separado do "revisar").
  const devisToExecuteCount = withoutTechQuery.data?.devisToExecute ?? 0;
  // F-lista-carros — contadores dos chips (mesmas consultas do Painel, cache).
  const boardQ = trpc.dossiers.operationBoard.useQuery(
    { companyId: companyId ?? 0, includeEntregues: true },
    { enabled: companyId !== null, staleTime: 30_000 },
  );
  const briefingQ = trpc.home.briefing.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null, staleTime: 30_000 },
  );
  // CEO 2026-10-31 — cartão "Faturados": faturas emitidas ainda não recebidas.
  const faturadosQ = trpc.dossiers.summary.useQuery(
    { companyId: companyId ?? 0, customerInvoiceStatus: "issued" },
    { enabled: companyId !== null, staleTime: 30_000 },
  );
  // CEO 2026-11-03 — com cliente filtrado, os cartões contam SÓ esse cliente
  // (antes mostravam a empresa inteira enquanto a lista era do cliente).
  const clienteDosCartoes = pinnedCustomerId ?? customerFilter;
  const cartaoCliente = {
    enabled: companyId !== null && clienteDosCartoes != null,
    staleTime: 30_000,
  };
  const baseCartao = { companyId: companyId ?? 0, customerId: clienteDosCartoes ?? undefined };
  const devisCliQ = trpc.dossiers.summary.useQuery(
    { ...baseCartao, devisToExecute: true },
    cartaoCliente,
  );
  const emRepCliQ = trpc.dossiers.summary.useQuery(
    { ...baseCartao, opStatus: "em_reparacao" },
    cartaoCliente,
  );
  const reparadoCliQ = trpc.dossiers.summary.useQuery(
    { ...baseCartao, onlyRepaired: true, customerInvoiceStatus: "pending" },
    cartaoCliente,
  );
  const faturadoCliQ = trpc.dossiers.summary.useQuery(
    { ...baseCartao, customerInvoiceStatus: "issued" },
    cartaoCliente,
  );
  type Chip = "all" | "toExecute" | "inRepair" | "repaired" | "invoiced" | "received" | "noTech";
  const chipAtivo: Chip | null = withoutTechnician
    ? "noTech"
    : devisToExecute
      ? "toExecute"
      : opStatus === "em_reparacao"
        ? "inRepair"
        : onlyRepaired && statusFilter === "pending"
          ? "repaired"
          : statusFilter === "issued" && !opStatus && !onlyRepaired
            ? "invoiced"
            : statusFilter === "paid" && !opStatus && !onlyRepaired
              ? "received"
              : statusFilter === "all" && !opStatus && !onlyRepaired && !degarnPending && !hasLines
                ? "all"
                : null;
  const aplicarChip = (chip: Chip) => {
    setWithoutTechnician(false);
    setDevisToExecute(false);
    setOpStatus(null);
    setOnlyRepaired(false);
    setDegarnPending(false);
    setHasLines(false);
    setStatusFilter("all");
    if (chip === "toExecute") setDevisToExecute(true);
    else if (chip === "inRepair") setOpStatus("em_reparacao");
    else if (chip === "repaired") {
      setOnlyRepaired(true);
      setStatusFilter("pending");
    } else if (chip === "invoiced") setStatusFilter("issued");
    else if (chip === "received") setStatusFilter("paid");
    else if (chip === "noTech") setWithoutTechnician(true);
    setOffset(0);
  };
  const CHIPS: Array<{ id: Chip; label: string; n: number | null; alerta?: boolean }> = [
    { id: "all", label: t("dossiers.chipAll"), n: null },
    { id: "toExecute", label: t("dossiers.chipToExecute"), n: devisToExecuteCount },
    {
      id: "inRepair",
      label: t("dossiers.chipInRepair"),
      // + espelhos ES→BS cujo carro original segue em reparação na ES.
      n: boardQ.data
        ? boardQ.data.counts.em_reparacao + (boardQ.data.espelhosEmReparacao ?? 0)
        : null,
    },
    {
      id: "repaired",
      label: t("dossiers.chipRepaired"),
      n: briefingQ.data?.toInvoiceCount ?? null,
    },
    { id: "invoiced", label: t("dossiers.chipInvoiced"), n: null },
    { id: "received", label: t("dossiers.chipReceived"), n: null },
    { id: "noTech", label: t("dossiers.chipNoTech"), n: withoutTechCount, alerta: true },
  ];
  // CEO 2026-10-31 — cartões de situação no topo da lista (mesmas cores dos
  // selos de cada carro). Clicar filtra; clicar de novo volta para "Todos".
  const CARDS: Array<{
    chip: Chip;
    label: string;
    cor: string;
    n: number | null;
    sub: string | null;
  }> = [
    {
      chip: "toExecute",
      label: t("vehicles.chipDevis"),
      cor: VEHICLE_STATUS_STYLE.devis!.bg,
      n:
        clienteDosCartoes != null
          ? (devisCliQ.data?.dossierCount ?? null)
          : withoutTechQuery.data
            ? devisToExecuteCount
            : null,
      sub: t("dossiers.cardDevisSub"),
    },
    {
      chip: "inRepair",
      label: t("vehicles.chipEmReparacao"),
      cor: VEHICLE_STATUS_STYLE.em_reparacao!.bg,
      n:
        clienteDosCartoes != null
          ? (emRepCliQ.data?.dossierCount ?? null)
          : boardQ.data
            ? boardQ.data.counts.em_reparacao + (boardQ.data.espelhosEmReparacao ?? 0)
            : null,
      sub: t("dossiers.cardInRepairSub"),
    },
    {
      chip: "repaired",
      label: t("vehicles.chipReparado"),
      cor: VEHICLE_STATUS_STYLE.reparado!.bg,
      n:
        clienteDosCartoes != null
          ? (reparadoCliQ.data?.dossierCount ?? null)
          : (briefingQ.data?.toInvoiceCount ?? null),
      sub:
        clienteDosCartoes != null
          ? reparadoCliQ.data
            ? `${formatMoney(Number(reparadoCliQ.data.facturadoTotal), { decimals: 0 })} ${t("dossiers.cardToInvoiceSuf")}`
            : null
          : briefingQ.data
            ? `${formatMoney(briefingQ.data.toInvoiceTotalHt, { decimals: 0 })} ${t("dossiers.cardToInvoiceSuf")}`
            : null,
    },
    {
      chip: "invoiced",
      label: t("vehicles.chipFaturado"),
      cor: VEHICLE_STATUS_STYLE.faturado!.bg,
      n:
        clienteDosCartoes != null
          ? (faturadoCliQ.data?.dossierCount ?? null)
          : (faturadosQ.data?.dossierCount ?? null),
      sub: (() => {
        const q = clienteDosCartoes != null ? faturadoCliQ.data : faturadosQ.data;
        return q
          ? `${formatMoney(q.facturadoTotal, { decimals: 0 })} ${t("dossiers.cardInvoicedSuf")}`
          : null;
      })(),
    },
  ];
  const chipsComCartao = new Set<Chip>(CARDS.map((c) => c.chip));

  // F-dossiers-kpis (2026-05-28) — KPIs do topo do painel. Reusa os mesmos
  // filtros do listing (q, customerId, status, etc) mas SEM paginação
  // (sortBy/limit/offset não afetam agregados). Cache de 30s no react-query
  // pra evitar refetch a cada digitação do campo de busca.
  const summaryFilters = useMemo(() => {
    const { sortBy: _s, limit: _l, offset: _o, oneRowPerVehicle: _v, ...rest } = filters;
    void _s;
    void _l;
    void _o;
    void _v;
    return rest;
  }, [filters]);
  const summaryQuery = trpc.dossiers.summary.useQuery(summaryFilters, {
    enabled: companyId !== null,
    staleTime: 30_000,
  });

  const utils = trpc.useUtils();
  // CEO 2026-10-28 — "Emitir fatura" da seleção abre o rascunho direto.
  const [loteDraftId, setLoteDraftId] = useState<number | null>(null);
  const criarRascunhoLote = trpc.invoiceDrafts.create.useMutation({
    onSuccess: (r) => setLoteDraftId(r.id),
    onError: (e) => {
      if (e.message.startsWith("DRAFT_EXISTS:")) {
        setLoteDraftId(Number(e.message.slice("DRAFT_EXISTS:".length)));
        return;
      }
      void modals.alert({
        title: t("dossiers.issueInvoiceBtn"),
        message: e.message,
        tone: "danger",
      });
    },
  });
  const restoreMutation = trpc.dossiers.restore.useMutation({
    onSuccess: () => utils.dossiers.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const softDeleteMutation = trpc.dossiers.softDelete.useMutation({
    onSuccess: () => utils.dossiers.invalidate(),
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  // F-dossiers-ux — bulk update do customer_invoice_status
  const bulkUpdateStatusMutation = trpc.dossiers.bulkUpdateCustomerInvoiceStatus.useMutation({
    onSuccess: (data) => {
      void modals.alert({
        message:
          `${t("dossiers.toastStatusUpdatedPre")} ${data.updated.length} dossier${data.updated.length === 1 ? "" : "s"}.` +
          (data.skipped.length > 0
            ? ` ${data.skipped.length} ${t(data.skipped.length === 1 ? "dossiers.toastSkippedWord" : "dossiers.toastSkippedWordPlural")} (${t("dossiers.toastSkippedSameStatusReason")}).`
            : ""),
      });
      utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  // F-bulk-tech-paid — marca/desfaz o pagamento ao técnico em lote (status das
  // service_lines; mesmo sinal do badge "Téc ·"). Mesmo padrão de toast.
  const bulkTechPayMutation = trpc.dossiers.bulkUpdateTechnicianPaymentStatus.useMutation({
    onSuccess: (data) => {
      void modals.alert({
        message:
          `${t("dossiers.toastStatusUpdatedPre")} ${data.updated.length} dossier${data.updated.length === 1 ? "" : "s"}.` +
          (data.skipped.length > 0
            ? ` ${data.skipped.length} ${t(data.skipped.length === 1 ? "dossiers.toastSkippedWord" : "dossiers.toastSkippedWordPlural")} (${t("dossiers.toastSkippedSameStatusReason")}).`
            : ""),
      });
      utils.dossiers.invalidate();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  // F-consolidated-invoice-review — a emissão saiu daqui: "Emitir fatura" agora
  // leva à página de revisão /faturas/nova (preview + confirmar) antes de efetivar.

  // F12.5 — bulk assign technician + bulk update commission
  const bulkAssignTechMutation = trpc.dossiers.bulkAssignTechnician.useMutation({
    onSuccess: (data) => {
      void modals.alert({
        message:
          `${t("dossiers.toastTechAssignedPre")} ${data.updated.length} dossier${data.updated.length === 1 ? "" : "s"}.` +
          (data.skipped.length > 0
            ? ` ${data.skipped.length} ${t(data.skipped.length === 1 ? "dossiers.toastSkippedWord" : "dossiers.toastSkippedWordPlural")}.`
            : ""),
      });
      utils.dossiers.invalidate();
      setAssignTechModalOpen(false);
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const bulkUpdateCommissionMutation = trpc.dossiers.bulkUpdateCommission.useMutation({
    onSuccess: (data) => {
      void modals.alert({
        message:
          `${t("dossiers.toastCommissionUpdatedPre")} ${data.updated.length} dossier${data.updated.length === 1 ? "" : "s"}.` +
          (data.skipped.length > 0
            ? ` ${data.skipped.length} ${t(data.skipped.length === 1 ? "dossiers.toastSkippedWord" : "dossiers.toastSkippedWordPlural")} (${t("dossiers.toastSkippedNoComplementaryReason")}).`
            : ""),
      });
      utils.dossiers.invalidate();
      setUpdateCommissionModalOpen(false);
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("dossiers.errorTitle"),
        message: `${t("dossiers.errorTitle")}: ${e.message}`,
        tone: "danger",
      }),
  });

  const [assignTechModalOpen, setAssignTechModalOpen] = useState(false);
  const [updateCommissionModalOpen, setUpdateCommissionModalOpen] = useState(false);
  // F-comm-cleanup C6 — modal admin para backfill em massa de linhas legacy.
  const [showPromoteLegacyModal, setShowPromoteLegacyModal] = useState(false);

  const [, setLocation] = useLocation();

  // F12 — Seleção múltipla. Hook antes do early return pra preservar regra
  // dos hooks. `selection` resolve a partir das rows da página atual.
  const data = listQuery.data;
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

  // F13.5 commit 3 — ordenação client-side da página corrente (não muda
  // pagination/offset do backend). Default = ordem do backend (data desc).
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  // F-dossiers-sel-totals — "Ver só selecionados": filtra a lista pros marcados
  // e mostra os totais (faturado / técnico / nº) numa faixa perto dos KPIs.
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Redesign fase 3 — preferências de visualização da tabela, persistidas.
  // "compact" aperta paddings/fonte; hiddenCols esconde colunas opcionais
  // (as estruturais — checkbox, data/dossier, veículo, ações — ficam sempre).
  const [compact, setCompact] = useState(
    () => localStorage.getItem("edsp.dossiers.compact") === "1",
  );
  // F-acesso-por-menu — gerente de chantier nunca vê a repartição (comissões).
  const { grantOnly } = useGrantOnly();
  const [hiddenCols, setHiddenCols] = useState<ReadonlySet<string>>(() => {
    try {
      const raw = localStorage.getItem("edsp.dossiers.hiddenCols");
      const set = new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
      // F-lista-carros — a "Repartição" (3 linhas por carro) sai da lista por
      // padrão; volta pelo menu "Colunas". Só na primeira vez (v2).
      if (!localStorage.getItem("edsp.dossiers.hiddenCols.v2")) {
        set.add("repartition");
        localStorage.setItem("edsp.dossiers.hiddenCols.v2", "1");
        localStorage.setItem("edsp.dossiers.hiddenCols", JSON.stringify([...set]));
      }
      return set;
    } catch {
      return new Set();
    }
  });
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const toggleCompact = () => {
    setCompact((v) => {
      localStorage.setItem("edsp.dossiers.compact", v ? "0" : "1");
      return !v;
    });
  };
  const toggleCol = (col: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      localStorage.setItem("edsp.dossiers.hiddenCols", JSON.stringify([...next]));
      return next;
    });
  };
  const sortedRows = useMemo(() => {
    if (sortKey == null) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const sa = sortValue(a, sortKey);
      const sb = sortValue(b, sortKey);
      if (sa == null && sb == null) return 0;
      if (sa == null) return 1; // nulls last
      if (sb == null) return -1;
      if (typeof sa === "number" && typeof sb === "number") {
        return sortDir === "asc" ? sa - sb : sb - sa;
      }
      const cmp = String(sa).localeCompare(String(sb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);
  const selection = useBulkSelection(sortedRows);

  // F-dossiers-sel-totals — totais dos dossiers marcados (faturado + pago ao
  // técnico), exibidos no painel de seleção. O valor do técnico segue a mesma
  // regra do CommissionCascade: amount armazenado, senão facturado × %; legacy
  // usa o valor legado.
  const selTotals = useMemo(() => {
    let faturado = 0;
    let tecnico = 0;
    let montagem = 0;
    for (const d of selection.selectedItems) {
      faturado += Number(d.facturadoTotal) || 0;
      tecnico += techPaidOf(d);
      montagem += montageOtherOf(d);
    }
    return { faturado, tecnico, montagem };
  }, [selection.selectedItems]);

  // F-dossiers-sel-totals — "Gerar relatório": PDF dos dossiers marcados com os
  // valores como estão na tela (faturado / pago técnico) + rodapé de totais.
  const selectionReportMutation = trpc.dossiers.selectionReportPdf.useMutation();
  async function downloadSelectionReport() {
    if (companyId == null) return;
    const reportRows = selection.selectedItems.map((d) => ({
      date: d.invoiceDate ?? "",
      invoice: d.customerInvoiceNumber ?? d.invoiceNumberExternal ?? "",
      plate: d.plate ?? "",
      vehicle: `${d.vehicleMakeSnapshot ?? ""} ${d.vehicleModelSnapshot ?? ""}`.trim(),
      customer: d.customerDisplayName ?? `#${d.customerId}`,
      technician: d.technicianName ?? "",
      faturado: Number(d.facturadoTotal) || 0,
      tecnico: techPaidOf(d),
    }));
    if (reportRows.length === 0) return;
    try {
      await abrirArquivo(async () => {
        const out = await selectionReportMutation.mutateAsync({ companyId, rows: reportRows });
        const bytes = Uint8Array.from(atob(out.base64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return url;
      });
    } catch (e) {
      void modals.alert({
        title: t("dossiers.reportErrorTitle"),
        message: e instanceof Error ? e.message : String(e),
        tone: "danger",
      });
    }
  }

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  if (!user) return null;

  const isAdmin =
    user.isSuperAdmin ||
    user.memberships.some((m) => m.companyId === companyId && m.active && m.role === "admin");

  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("dossiers.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const total = data?.total ?? 0;
  const customers = customersQuery.data?.rows ?? [];

  // F-dossiers-sel-totals — quando "ver só selecionados" está ligado (e há
  // seleção), a tabela mostra apenas os marcados da página atual.
  const displayRows =
    showOnlySelected && selection.count > 0
      ? sortedRows.filter((d) => selection.isSelected(d.id))
      : sortedRows;

  // F-dossiers-kpis — booleano de "tem filtro ativo?" pra mudar o label
  // dos KPIs (filtrado vs. geral). Considera os campos que o usuário pode
  // interagir; ignora flags estruturais como includeDeleted/includeTestFixtures.
  const hasActiveFilter =
    Boolean(q) ||
    customerFilter !== null ||
    technicianFilter !== null ||
    statusFilter !== "all" ||
    techPayFilter !== "all" ||
    Boolean(from) ||
    Boolean(to) ||
    invoiceNumberFrom.trim() !== "" ||
    invoiceNumberTo.trim() !== "" ||
    withoutTechnician ||
    devisToExecute ||
    hasLines ||
    onlyRepaired ||
    degarnPending ||
    opStatus != null ||
    technicianPaymentPending;

  // Redesign fase 2 — contagem de filtros ativos p/ o chip ao lado do "limpar".
  // Período e faixa de fatura contam como 1 filtro cada (são um range só).
  const activeFilterCount = [
    Boolean(q),
    customerFilter !== null,
    technicianFilter !== null,
    statusFilter !== "all",
    techPayFilter !== "all",
    Boolean(from) || Boolean(to),
    invoiceNumberFrom.trim() !== "" || invoiceNumberTo.trim() !== "",
    withoutTechnician,
    devisToExecute,
    hasLines,
    onlyRepaired,
    degarnPending,
    opStatus != null,
    technicianPaymentPending,
  ].filter(Boolean).length;

  // F-dossiers-filters-ux — limpa todos os filtros (não mexe em empresa/ordenação).
  const clearFilters = () => {
    setQ("");
    setCustomerFilter(null);
    setTechnicianFilter(null);
    setStatusFilter("all");
    setTechPayFilter("all");
    setFrom("");
    setTo("");
    setInvoiceNumberFrom("");
    setInvoiceNumberTo("");
    setWithoutTechnician(false);
    setDevisToExecute(false);
    setHasLines(false);
    setOnlyRepaired(false);
    setDegarnPending(false);
    setOpStatus(null);
    setTechnicianPaymentPending(false);
    setOffset(0);
  };

  return (
    <Shell>
      {/* Redesign V4 — page header (.ph): título + subtítulo + ações. */}
      <div className="ph">
        <div>
          <h1>{t("dossiers.title")}</h1>
          <p>
            {accessibleCompanies.find((c) => c.id === companyId)?.name ?? "—"} ·{" "}
            {/* CEO 2026-10-26 — a lista tem uma linha por veículo: conta veículos. */}
            {listQuery.data?.total ?? 0}{" "}
            {(listQuery.data?.total ?? 0) === 1 ? t("dashboard.carWord") : t("dashboard.carsWord")}
          </p>
        </div>
        <div className="ph__actions">
          {canEdit && isAdmin && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setShowPromoteLegacyModal(true)}
              title={t("dossiers.recalcCommissionsTitle")}
            >
              {t("dossiers.recalcCommissionsBtn")}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setShowWizard(true)}
              disabled={customers.length === 0}
            >
              <Plus size={15} /> {t("dossiers.newDossierBtn")}
            </button>
          )}
        </div>
      </div>

      {/* F7.1-2 — banner de alerta para dossiers sem técnico */}
      {/* Cores FIXAS (mesmo padrão da faixa do Painel): fundo claro + texto
          escuro — legível nos temas claro E escuro (tokens temáticos aqui
          deixavam o texto invisível no claro). */}
      {/* F-lista-carros — os dois alertas viraram chips de situação com contagem
          (o chip ativo é o filtro em uso; "Todos" limpa). */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="dossiers-status-cards">
        {CARDS.map((c) => {
          const on = chipAtivo === c.chip;
          return (
            <button
              key={c.chip}
              type="button"
              onClick={() => aplicarChip(on ? "all" : c.chip)}
              aria-pressed={on}
              data-testid={`dossiers-card-${c.chip}`}
              className="flex flex-col items-start gap-0.5 rounded-xl px-4 py-3 text-left transition-shadow hover:shadow-md"
              style={{
                background: "var(--surface-card)",
                border: `1px solid ${on ? c.cor : "var(--border)"}`,
                borderLeft: `6px solid ${c.cor}`,
                boxShadow: on ? `0 0 0 2px ${c.cor}40` : undefined,
              }}
            >
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: c.cor }}>
                {c.label}
              </span>
              <span
                className="text-3xl font-bold tabular-nums leading-tight"
                style={{ color: "var(--text-strong)", fontFamily: "var(--font-display)" }}
              >
                {c.n ?? "…"}
              </span>
              {c.sub && (
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {c.sub}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="dossiers-chips">
        {CHIPS.filter(
          (c) => !chipsComCartao.has(c.id) && (!c.alerta || (c.n ?? 0) > 0 || chipAtivo === c.id),
        ).map((c) => {
          const on = chipAtivo === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => aplicarChip(c.id)}
              className={`inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
                on
                  ? "border-lime bg-lime text-on-lime"
                  : c.alerta
                    ? "border-amber-400 bg-amber-50 text-amber-800 hover:border-amber-500"
                    : "border-neutral-700 bg-neutral-900 text-text-secondary hover:border-lime hover:text-lime"
              }`}
              data-testid={`chip-${c.id}`}
              aria-pressed={on}
            >
              {c.alerta ? "⚠ " : ""}
              {c.label}
              {c.n != null && (
                <span
                  className={`rounded-full px-1.5 text-[10px] tabular-nums ${on ? "bg-black/10" : "bg-neutral-800 text-text-muted"}`}
                >
                  {c.n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* F-dossiers-kpis (2026-05-28) + F-dossiers-kpis-v2 — Painel de KPIs.
          6 cards, TODOS respeitam os filtros do painel. Quando hasActiveFilter
          é true mostram "Filtrado"; sem filtro = "Geral" (e "Técnicos a pagar"
          sem filtro = total global da empresa, como antes). Clicar no card
          "Técnicos a pagar" aplica o filtro technicianPaymentPending. */}
      {!kpisExpanded ? (
        (() => {
          const sd = summaryQuery.data;
          const orc = Number(sd?.orcamentoTotal ?? 0);
          const fac = Number(sd?.facturadoTotal ?? 0);
          const pct = orc > 0 ? Math.round((fac / orc) * 100) : null;
          // CEO 2026-10-31 ("sem vida, texto apagado") — cada total vira um bloco
          // com rótulo legível e valor grande; cor só onde tem significado.
          const temTecnico = Number(sd?.technicianAmountToPay ?? 0) > 0;
          const bloco = (
            key: string,
            label: string,
            value: string,
            cor: string,
            extra?: ReactNode,
          ) => (
            <div key={key} className="flex min-w-0 flex-col gap-0.5 px-4 py-3">
              <span
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: "var(--text-body)" }}
              >
                {label}
              </span>
              <span
                className="num whitespace-nowrap text-2xl font-bold leading-tight tabular-nums"
                style={{ color: cor, fontFamily: "var(--font-display)" }}
              >
                {value}
              </span>
              {extra}
            </div>
          );
          return (
            <div
              className="flex flex-col rounded-xl border lg:flex-row lg:items-stretch"
              style={{
                background: "var(--surface-card)",
                borderColor: "var(--border-strong)",
                boxShadow: "var(--shadow-sm)",
              }}
              data-testid="dossiers-summary-strip"
            >
              <div className="grid w-full min-w-0 grid-cols-2 sm:grid-cols-3 lg:flex lg:w-auto lg:flex-1 lg:divide-x lg:divide-[var(--divider)]">
                {bloco(
                  "n",
                  `${t("dossiers.statDossiersWord")}${hasActiveFilter ? ` · ${t("dossiers.statFiltered")}` : ""}`,
                  String(sd?.dossierCount ?? "…"),
                  "var(--text-strong)",
                )}
                {orc > 0 && bloco("devis", t("dossiers.statDevis"), cm(orc), "var(--text-strong)")}
                {fac > 0 &&
                  bloco(
                    "fac",
                    t("dossiers.statInvoiced"),
                    cm(fac),
                    "var(--text-strong)",
                    pct != null && (
                      <span className="mt-1 flex items-center gap-2">
                        <span
                          className="h-1.5 w-16 overflow-hidden rounded-full"
                          style={{ background: "var(--surface-muted)" }}
                        >
                          <span
                            className="block h-full rounded-full"
                            style={{ width: `${Math.min(100, pct)}%`, background: "var(--accent)" }}
                          />
                        </span>
                        <span
                          className="text-xs font-semibold tabular-nums"
                          style={{ color: "var(--text-body)" }}
                        >
                          {pct}% {t("dossiers.statOfDevis")}
                        </span>
                      </span>
                    ),
                  )}
                {Number(sd?.lucroBruto ?? 0) !== 0 &&
                  bloco(
                    "lucro",
                    t("dossiers.statGross"),
                    cm(sd?.lucroBruto),
                    Number(sd?.lucroBruto ?? 0) < 0 ? "var(--danger-500)" : "var(--success-500)",
                  )}
                {Number(sd?.aReceberCliente ?? 0) > 0 &&
                  bloco(
                    "receber",
                    t("dossiers.statReceivable"),
                    cm(sd?.aReceberCliente),
                    "var(--info-500)",
                  )}
                {temTecnico && (
                  <button
                    key="tec"
                    type="button"
                    onClick={() => {
                      setTechnicianPaymentPending((v) => !v);
                      setOffset(0);
                    }}
                    aria-pressed={technicianPaymentPending}
                    className="flex min-w-0 flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors hover:bg-[var(--warning-50)]"
                    style={
                      technicianPaymentPending
                        ? {
                            background: "var(--warning-50)",
                            boxShadow: "inset 0 -3px 0 var(--warning-500)",
                          }
                        : undefined
                    }
                    title={t("dossiers.statTechPayTitle")}
                    data-testid="dossiers-summary-techpay"
                  >
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wider"
                      style={{ color: "var(--text-body)" }}
                    >
                      {t("dossiers.statTechPay")}
                    </span>
                    <span
                      className="num whitespace-nowrap text-2xl font-bold leading-tight tabular-nums"
                      style={{ color: "var(--warning-500)", fontFamily: "var(--font-display)" }}
                    >
                      {cm(sd?.technicianAmountToPay)}
                    </span>
                    <span className="text-xs font-semibold" style={{ color: "var(--text-body)" }}>
                      {technicianPaymentPending
                        ? `✓ ${t("dossiers.statTechPayOn")}`
                        : t("dossiers.statTechPayHint")}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex w-full items-center justify-end px-3 pb-3 lg:w-auto lg:pb-0">
                <button
                  type="button"
                  onClick={toggleKpis}
                  className={CHIP_OFF}
                  data-testid="dossiers-kpis-expand"
                >
                  <ChevronDown size={15} aria-hidden="true" />
                  {t("dossiers.kpisExpand")}
                </button>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="space-y-1">
          <div className="flex justify-end">
            <button type="button" onClick={toggleKpis} className={CHIP_OFF}>
              <ChevronUp size={15} aria-hidden="true" />
              {t("dossiers.kpisCollapse")}
            </button>
          </div>
          <KpiPanel
            loading={summaryQuery.isLoading}
            hasFilter={hasActiveFilter}
            dossierCount={summaryQuery.data?.dossierCount ?? 0}
            orcamentoTotal={summaryQuery.data?.orcamentoTotal ?? "0"}
            facturadoTotal={summaryQuery.data?.facturadoTotal ?? "0"}
            facturadoNetTotal={summaryQuery.data?.facturadoNetTotal ?? "0"}
            avoirsCaTotal={summaryQuery.data?.avoirsCaTotal ?? "0"}
            avoirsLossTotal={summaryQuery.data?.avoirsLossTotal ?? "0"}
            lucroPrevisto={summaryQuery.data?.lucroPrevisto ?? "0"}
            lucroBruto={summaryQuery.data?.lucroBruto ?? "0"}
            comissoesTotal={summaryQuery.data?.comissoesTotal ?? "0"}
            aReceberCliente={summaryQuery.data?.aReceberCliente ?? "0"}
            technicianAmountToPay={summaryQuery.data?.technicianAmountToPay ?? "0"}
            technicianDossiersToPayCount={summaryQuery.data?.technicianDossiersToPayCount ?? 0}
            technicianFilterActive={technicianPaymentPending}
            onToggleTechnicianFilter={() => {
              setTechnicianPaymentPending((v) => !v);
              setOffset(0);
            }}
          />
        </div>
      )}

      {/* F-dossiers-sel-totals — faixa da seleção: ver só marcados + totais. */}
      {selection.count > 0 && (
        <div
          className="flex items-center gap-3 flex-wrap rounded-lg border px-3 py-2"
          style={{ background: "var(--surface-card)", borderColor: "var(--border)" }}
        >
          <button
            onClick={() => setShowOnlySelected((v) => !v)}
            className={`rounded-md border px-3 py-1 text-xs ${
              showOnlySelected
                ? "border-lime bg-lime/10 text-lime"
                : "border-neutral-700 text-neutral-300 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-600"
            }`}
            title={t("dossiers.onlySelectedTitle")}
          >
            {showOnlySelected
              ? `✓ ${t("dossiers.onlySelectedActive")}`
              : `${t("dossiers.onlySelectedIdle")} (${selection.count})`}
          </button>
          <span className="text-sm" style={{ color: "var(--text-muted)" }}>
            {selection.count} dossier{selection.count === 1 ? "" : "s"} ·{" "}
            {t("dossiers.faturadoLabel")}{" "}
            <b style={{ color: "var(--text-strong)" }}>
              {formatMoney(selTotals.faturado, { decimals: 2 })}
            </b>{" "}
            · {t("dossiers.paidTechnicianLabel")}{" "}
            <b className="text-lime">{formatMoney(selTotals.tecnico, { decimals: 2 })}</b>
            {selTotals.montagem > 0 && (
              <>
                {" · "}
                {t("dossiers.montageOtherLabel")}{" "}
                <b className="text-lime">{formatMoney(selTotals.montagem, { decimals: 2 })}</b>
              </>
            )}
          </span>
        </div>
      )}

      {/* ─── F-layout-L1 — painel de filtros: linha compacta sempre visível
          (busca + empresa + botão Filtros); o resto abre sob demanda. ─────── */}
      <div
        className="space-y-4 rounded-xl border p-4"
        style={FILTER_PANEL_STYLE}
        data-testid="dossiers-filter-panel"
      >
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label={t("dossiers.searchLabel")} className="grow max-w-sm">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("dossiers.searchPlaceholder")}
              className={inputClass + " w-full"}
            />
          </FilterField>
          <FilterField label={t("dossiers.companyLabel")}>
            <select
              value={companyId ?? ""}
              onChange={(e) => {
                setCompanyId(Number(e.target.value));
                setOffset(0);
              }}
              className={inputClass + " w-56"}
            >
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FilterField>
          <div className="flex-1" />
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className={CHIP_OFF}
              title={t("dossiers.clearFiltersTitle")}
            >
              <X size={15} aria-hidden="true" />
              {t("dossiers.clearFilters")}
            </button>
          )}
          <button
            type="button"
            onClick={toggleFiltersOpen}
            aria-expanded={filtersOpen}
            className={filtersOpen || activeFilterCount > 0 ? CHIP_ON : CHIP_OFF}
            data-testid="dossiers-filters-toggle"
          >
            <SlidersHorizontal size={15} aria-hidden="true" />
            {t("dossiers.filtersBtn")}
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-lime px-1.5 text-[11px] font-bold text-on-lime">
                {activeFilterCount}
              </span>
            )}
            {filtersOpen ? (
              <ChevronUp size={15} aria-hidden="true" />
            ) : (
              <ChevronDown size={15} aria-hidden="true" />
            )}
          </button>
        </div>

        {filtersOpen && (
          <>
            {/* Selects principais */}
            <div className="grid grid-cols-1 gap-x-3 gap-y-3 border-t border-[var(--divider)] pt-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <FilterField label={t("dossiers.customerLabel")}>
                {/* CEO 2026-10-28 — alfinete ao lado do select (embaixo, esticava a linha). */}
                <div className="flex items-center gap-1">
                  <select
                    value={pinnedCustomerId ?? customerFilter ?? ""}
                    disabled={pinnedCustomerId != null}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustomerFilter(v === "" ? null : Number(v));
                      setOffset(0);
                    }}
                    className={inputClass + " w-full disabled:opacity-60"}
                  >
                    <option value="">{t("dossiers.optAllCustomers")}</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </select>
                  {pinnedCustomerId == null && customerFilter != null && companyId != null && (
                    <button
                      type="button"
                      title={t("erpShell.pinTitle")}
                      onClick={() => {
                        const c = customers.find((x) => x.id === customerFilter);
                        if (c)
                          setPin({
                            kind: "customer",
                            id: c.id,
                            label: c.displayName,
                            companyId,
                          });
                      }}
                      className={CHIP_OFF + " shrink-0 !px-2"}
                    >
                      📌
                    </button>
                  )}
                </div>
              </FilterField>
              <FilterField label={t("dossiers.technicianLabel")}>
                <select
                  value={technicianFilter ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTechnicianFilter(v === "" ? null : Number(v));
                    setOffset(0);
                  }}
                  className={inputClass + " w-full"}
                  title={t("dossiers.filterTechnicianTitle")}
                >
                  <option value="">{t("dossiers.optAllTechnicians")}</option>
                  {(techniciansQuery.data?.rows ?? []).map((tech) => (
                    <option key={tech.id} value={tech.id}>
                      {tech.name}
                    </option>
                  ))}
                </select>
              </FilterField>
              {mirrorProformas.length > 0 && (
                <FilterField label={t("dossiers.proformaFilterLabel")}>
                  <select
                    value={proformaRefFilter ?? ""}
                    onChange={(e) => {
                      setProformaRefFilter(e.target.value || null);
                      setOffset(0);
                    }}
                    className={inputClass + " w-full"}
                    title={t("dossiers.proformaFilterTitle")}
                    data-testid="proforma-filter"
                  >
                    <option value="">{t("dossiers.optAllProformas")}</option>
                    {mirrorProformas.map((pr) => (
                      <option key={pr.reference} value={pr.reference}>
                        {pr.reference} · {pr.technicianNames ?? "?"} · {pr.toInvoice}/{pr.carCount}{" "}
                        {t("dossiers.proformaOptSuffix")}
                      </option>
                    ))}
                  </select>
                </FilterField>
              )}
              <FilterField label={t("dossiers.invoiceStatusLabel")}>
                <select
                  value={withoutTechnician ? "without_technician" : statusFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "without_technician") {
                      setWithoutTechnician(true);
                      setStatusFilter("all");
                    } else {
                      setWithoutTechnician(false);
                      setStatusFilter(v);
                    }
                    setOffset(0);
                  }}
                  className={inputClass + " w-full"}
                >
                  <option value="all">{t("dossiers.optAllInvoiceStatus")}</option>
                  <option value="pending">{t("dossiers.optPending")}</option>
                  <option value="issued">{t("dossiers.optIssued")}</option>
                  <option value="paid">{t("dossiers.optReceived")}</option>
                  <option value="cancelled">{t("dossiers.optCancelled")}</option>
                  <option value="without_technician">⚠ {t("dossiers.optWithoutTechnician")}</option>
                </select>
              </FilterField>
              {/* Dropdown dedicado: pagamento ao técnico (col PAG. TEC). */}
              <FilterField label={t("dossiers.techPayLabel")}>
                <select
                  value={techPayFilter}
                  onChange={(e) => {
                    setTechPayFilter(e.target.value as "all" | "pending" | "paid");
                    setOffset(0);
                  }}
                  className={inputClass + " w-full"}
                  title={t("dossiers.filterTechPayTitle")}
                >
                  <option value="all">{t("dossiers.optAllTechPay")}</option>
                  <option value="pending">{t("dossiers.optTechPayPending")}</option>
                  <option value="paid">{t("dossiers.optTechPayPaid")}</option>
                </select>
              </FilterField>
            </div>

            {/* Ranges + ordenação + opções */}
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3 border-t border-[var(--divider)] pt-4">
              <FilterField label={t("dossiers.periodLabel")}>
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      setOffset(0);
                    }}
                    className={inputClass + " w-36"}
                    title={t("dossiers.dateFromTitle")}
                  />
                  <span style={{ color: "var(--text-muted)" }}>→</span>
                  <input
                    type="date"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                      setOffset(0);
                    }}
                    className={inputClass + " w-36"}
                    title={t("dossiers.dateToTitle")}
                  />
                </div>
              </FilterField>
              <FilterField label={t("dossiers.invoiceRangeLabel")}>
                <div className="flex items-center gap-1.5">
                  <input
                    value={invoiceNumberFrom}
                    onChange={(e) => {
                      setInvoiceNumberFrom(e.target.value);
                      setOffset(0);
                    }}
                    placeholder={t("dossiers.rangeFromPlaceholder")}
                    className={inputClass + " w-32"}
                    title={t("dossiers.invoiceFromTitle")}
                  />
                  <span style={{ color: "var(--text-muted)" }}>→</span>
                  <input
                    value={invoiceNumberTo}
                    onChange={(e) => {
                      setInvoiceNumberTo(e.target.value);
                      setOffset(0);
                    }}
                    placeholder={t("dossiers.rangeToPlaceholder")}
                    className={inputClass + " w-32"}
                    title={t("dossiers.invoiceToTitle")}
                  />
                </div>
              </FilterField>
              {/* F-dossiers-ux — Sort global (backend): campo + direção (crescente/
              decrescente). Default = nº de fatura crescente (segue a sequência). */}
              <FilterField label={t("dossiers.sortByLabel")}>
                <div className="flex items-center gap-1">
                  <select
                    value={sortBy.replace(/_(asc|desc)$/, "")}
                    onChange={(e) => {
                      const dir = sortBy.endsWith("_asc") ? "asc" : "desc";
                      setSortBy(`${e.target.value}_${dir}` as typeof sortBy);
                      setOffset(0);
                    }}
                    className={inputClass + " w-auto max-w-80"}
                    title={t("dossiers.sortFieldTitle")}
                  >
                    <option value="activity">{t("dossiers.sortActivity")}</option>
                    <option value="invoice_number">{t("dossiers.colInvoiceNumber")}</option>
                    <option value="date">{t("dossiers.dateLabel")}</option>
                    <option value="facturado">{t("dossiers.faturadoLabel")}</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const field = sortBy.replace(/_(asc|desc)$/, "");
                      const next = sortBy.endsWith("_asc") ? "desc" : "asc";
                      setSortBy(`${field}_${next}` as typeof sortBy);
                      setOffset(0);
                    }}
                    className={CHIP_OFF}
                    title={
                      sortBy.endsWith("_asc")
                        ? t("dossiers.sortAscTitle")
                        : t("dossiers.sortDescTitle")
                    }
                  >
                    {sortBy.endsWith("_asc") ? (
                      <ArrowUpNarrowWide size={15} aria-hidden="true" />
                    ) : (
                      <ArrowDownWideNarrow size={15} aria-hidden="true" />
                    )}
                    {sortBy.endsWith("_asc") ? t("dossiers.sortAsc") : t("dossiers.sortDesc")}
                  </button>
                </div>
              </FilterField>

              {/* Redesign fase 3 — Vue compacte + seletor de colunas visíveis.
                  CEO 2026-10-28 — agrupados à direita, mesma altura dos campos. */}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={toggleCompact}
                  aria-pressed={compact}
                  className={compact ? CHIP_ON : CHIP_OFF}
                  title={t("dossiers.viewCompact")}
                >
                  <Rows3 size={15} aria-hidden="true" />
                  {t("dossiers.viewCompact")}
                </button>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setColMenuOpen((v) => !v)}
                    className={hiddenCols.size > 0 ? CHIP_ON : CHIP_OFF}
                    title={t("dossiers.columnsBtn")}
                  >
                    <Columns3 size={15} aria-hidden="true" />
                    {t("dossiers.columnsBtn")}
                    {hiddenCols.size > 0
                      ? ` · ${TOGGLEABLE_COLS.length - hiddenCols.size}/${TOGGLEABLE_COLS.length}`
                      : ""}
                    <ChevronDown size={15} aria-hidden="true" />
                  </button>
                  {colMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setColMenuOpen(false)} />
                      <div
                        className="absolute right-0 top-11 z-20 min-w-[200px] rounded-lg border p-1 shadow-xl"
                        style={{
                          background: "var(--surface-card)",
                          borderColor: "var(--border-strong)",
                        }}
                      >
                        {TOGGLEABLE_COLS.map((col) => (
                          <label
                            key={col.id}
                            className="flex w-full cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-[13px] text-text-primary hover:bg-[var(--surface-muted)]"
                          >
                            <input
                              type="checkbox"
                              checked={!hiddenCols.has(col.id)}
                              onChange={() => toggleCol(col.id)}
                              className="accent-lime"
                            />
                            {t(col.labelKey)}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                <label
                  className={(includeDeleted ? CHIP_ON : CHIP_OFF) + " cursor-pointer select-none"}
                >
                  <input
                    type="checkbox"
                    checked={includeDeleted}
                    onChange={(e) => {
                      setIncludeDeleted(e.target.checked);
                      setOffset(0);
                    }}
                    className="accent-lime"
                  />
                  <Trash2 size={15} aria-hidden="true" />
                  {t("dossiers.includeDeleted")}
                </label>
              </div>
            </div>
          </>
        )}
      </div>

      {/* CEO 2026-10-19 — ordem visível fora do painel de filtros (que fica
          recolhido): mostra o campo e um botão que inverte a direção. */}
      <div
        className="mb-1 flex items-center justify-end gap-2 text-[13px]"
        style={{ color: "var(--text-muted)" }}
        data-testid="dossiers-sort-bar"
      >
        <span>
          {t("dossiers.sortByLabel")}:{" "}
          <b style={{ color: "var(--text-strong)" }}>
            {(() => {
              const f = sortBy.replace(/_(asc|desc)$/, "");
              return f === "activity"
                ? t("dossiers.sortActivity")
                : f === "invoice_number"
                  ? t("dossiers.colInvoiceNumber")
                  : f === "date"
                    ? t("dossiers.dateLabel")
                    : t("dossiers.faturadoLabel");
            })()}
          </b>
        </span>
        <button
          type="button"
          onClick={() => {
            const field = sortBy.replace(/_(asc|desc)$/, "");
            const next = sortBy.endsWith("_asc") ? "desc" : "asc";
            setSortBy(`${field}_${next}` as typeof sortBy);
            setOffset(0);
          }}
          className={CHIP_OFF + " !h-8"}
          title={sortBy.endsWith("_asc") ? t("dossiers.sortAscTitle") : t("dossiers.sortDescTitle")}
          data-testid="dossiers-sort-dir"
        >
          {sortBy.endsWith("_asc") ? (
            <ArrowUpNarrowWide size={15} aria-hidden="true" />
          ) : (
            <ArrowDownWideNarrow size={15} aria-hidden="true" />
          )}
          {sortBy.endsWith("_asc") ? t("dossiers.sortAsc") : t("dossiers.sortDesc")}
        </button>
      </div>
      <div
        className={`rounded-lg border border-neutral-800 bg-neutral-900 ${isMobile ? "overflow-hidden" : "overflow-x-auto"}`}
      >
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-neutral-500">{t("dossiers.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-neutral-500">{t("dossiers.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && !isMobile && (
          <table className={`dz-tbl w-full min-w-[1080px] ${compact ? "dz-tbl--compact" : ""}`}>
            <thead>
              <tr>
                <th className="w-10 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={selection.isAllSelected}
                    onChange={(e) => (e.target.checked ? selection.selectAll() : selection.clear())}
                    title={t("dossiers.selectPageTitle")}
                    className="accent-lime"
                  />
                </th>
                <SortableTh
                  label={t("dossiers.colDateDossier")}
                  sortKey="invoiceDate"
                  active={sortKey}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="w-32 text-left"
                />
                <SortableTh
                  label={t("dossiers.colVehicule")}
                  sortKey="plate"
                  active={sortKey}
                  dir={sortDir}
                  onClick={toggleSort}
                  className="w-36 text-left"
                />
                {!hiddenCols.has("technician") && (
                  <th className="text-left px-2 py-2 w-32">{t("dossiers.colTechnician")}</th>
                )}
                {!hiddenCols.has("customer") && (
                  <SortableTh
                    label={t("dossiers.colCustomer")}
                    sortKey="customer"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    className="text-left min-w-[140px]"
                  />
                )}
                {!hiddenCols.has("valeurs") && (
                  <SortableTh
                    label={t("dossiers.colValeurs")}
                    sortKey="facturado"
                    active={sortKey}
                    dir={sortDir}
                    onClick={toggleSort}
                    className="w-32 text-right"
                  />
                )}
                {!hiddenCols.has("repartition") && !grantOnly && (
                  <th className="text-left px-2 py-2 w-44" title={t("dossiers.colCommTitle")}>
                    {t("dossiers.colRepartition")}
                  </th>
                )}
                {!hiddenCols.has("statut") && (
                  <th className="text-left px-2 py-2 w-40">{t("dossiers.colStatut")}</th>
                )}
                <th className="w-40 px-2 py-2 text-left">{t("dossiers.colAction")}</th>
                <th className="px-2 py-2 w-28 text-right">{t("dossiers.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {displayRows.map((d) => (
                <DossierRow
                  key={d.id}
                  onOpenVehicle={setFichaVeiculo}
                  d={d}
                  selected={selection.isSelected(d.id)}
                  onToggleSelect={() => selection.toggle(d.id)}
                  onSoftDelete={async () => {
                    const reason = await modals.promptReason({
                      title: `${t("dossiers.deleteDossierTitlePre")} ${d.dossierCode}`,
                      message: t("dossiers.deleteDossierMessage"),
                      placeholder: t("dossiers.deleteReasonPlaceholder"),
                      confirmLabel: t("dossiers.deleteConfirmLabel"),
                      tone: "danger",
                      required: true,
                    });
                    if (!reason) return;
                    softDeleteMutation.mutate({
                      companyId: companyId!,
                      dossierId: d.id,
                      reason,
                    });
                  }}
                  onRestore={() =>
                    restoreMutation.mutate({
                      companyId: companyId!,
                      dossierId: d.id,
                    })
                  }
                  isAdmin={isAdmin}
                  restorePending={restoreMutation.isPending}
                  deletePending={softDeleteMutation.isPending}
                  companyName={billingCompanyName}
                  hiddenCols={grantOnly ? new Set([...hiddenCols, "repartition"]) : hiddenCols}
                />
              ))}
            </tbody>
          </table>
        )}
        {data && data.rows.length > 0 && isMobile && (
          <div data-testid="dossiers-cards">
            <label className="flex items-center gap-3 border-b border-neutral-800 px-3 py-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={selection.isAllSelected}
                onChange={(e) => (e.target.checked ? selection.selectAll() : selection.clear())}
                className="h-5 w-5 accent-lime"
              />
              {t("dossiers.selectPageTitle")} · {displayRows.length}
            </label>
            <div className="divide-y divide-neutral-800">
              {displayRows.map((d) => (
                <DossierRow
                  key={d.id}
                  layout="card"
                  onOpenVehicle={setFichaVeiculo}
                  d={d}
                  selected={selection.isSelected(d.id)}
                  onToggleSelect={() => selection.toggle(d.id)}
                  onSoftDelete={async () => {
                    const reason = await modals.promptReason({
                      title: `${t("dossiers.deleteDossierTitlePre")} ${d.dossierCode}`,
                      message: t("dossiers.deleteDossierMessage"),
                      placeholder: t("dossiers.deleteReasonPlaceholder"),
                      confirmLabel: t("dossiers.deleteConfirmLabel"),
                      tone: "danger",
                      required: true,
                    });
                    if (!reason) return;
                    softDeleteMutation.mutate({
                      companyId: companyId!,
                      dossierId: d.id,
                      reason,
                    });
                  }}
                  onRestore={() =>
                    restoreMutation.mutate({
                      companyId: companyId!,
                      dossierId: d.id,
                    })
                  }
                  isAdmin={isAdmin}
                  restorePending={restoreMutation.isPending}
                  deletePending={softDeleteMutation.isPending}
                  companyName={billingCompanyName}
                  hiddenCols={grantOnly ? new Set([...hiddenCols, "repartition"]) : hiddenCols}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? t("dashboard.carWord") : t("dashboard.carsWord")}
          testId="dossiers-pagination"
        />
      )}

      {canEdit && showWizard && companyId !== null && (
        <DossierWizard
          companyId={companyId}
          accessibleCompanies={accessibleCompanies}
          onClose={() => setShowWizard(false)}
          onCreated={() => {
            setShowWizard(false);
            utils.dossiers.invalidate();
          }}
        />
      )}

      {/* F12 — Barra de ações em massa */}
      <BulkActionBar
        count={selection.count}
        onClear={selection.clear}
        label={`${selection.count} dossier${selection.count === 1 ? "" : "s"} ${t(selection.count === 1 ? "dossiers.bulkSelectedWord" : "dossiers.bulkSelectedWordPlural")}`}
        summary={
          <span className="whitespace-nowrap">
            {t("dossiers.faturadoLabel")}{" "}
            <b className="text-neutral-200">{formatMoney(selTotals.faturado, { decimals: 2 })}</b>
            {" · "}
            {t("dossiers.technicianLabel")}{" "}
            <b className="text-lime">{formatMoney(selTotals.tecnico, { decimals: 2 })}</b>
            {selTotals.montagem > 0 && (
              <>
                {" · "}
                {t("dossiers.montageOtherLabel")}{" "}
                <b className="text-lime">{formatMoney(selTotals.montagem, { decimals: 2 })}</b>
              </>
            )}
          </span>
        }
      >
        <BulkGroup label={t("dossiers.bulkGroupExport")}>
          <button
            onClick={() => {
              const headers = [
                t("dossiers.csvDate"),
                t("dossiers.csvInvoiceNumber"),
                t("dossiers.csvCode"),
                t("dossiers.csvPlate"),
                t("dossiers.csvMake"),
                t("dossiers.csvModel"),
                t("dossiers.csvTechnician"),
                t("dossiers.csvCustomer"),
                t("dossiers.csvDevis"),
                t("dossiers.csvFacturado"),
                t("dossiers.csvBenefice"),
                t("dossiers.csvInvoiceStatus"),
                t("dossiers.csvTechInvoiceNumber"),
                t("dossiers.csvTechInvoiceStatus"),
              ];
              const csvRows = selection.selectedItems.map((d) => {
                const benefice = computeBenefice({
                  facturado: d.facturadoTotal,
                  clientPct: d.primaryLineClientPctF8,
                  techPct: d.primaryLineTechnicianPctF8,
                  intermPct: d.primaryLineIntermediaryPctF8,
                  eurostormPct: d.primaryLineEurostormPctF8,
                  eurostormAmount: d.eurostormAmountTotal,
                  legacyTechAmount: d.primaryLineTechnicianCommissionAmount,
                  commissionModel: d.primaryLineCommissionModel,
                });
                return [
                  d.invoiceDate ?? "",
                  d.customerInvoiceNumber ?? d.invoiceNumberExternal ?? "",
                  d.dossierCode,
                  d.plate ?? "",
                  d.vehicleMakeSnapshot ?? "",
                  d.vehicleModelSnapshot ?? "",
                  d.technicianName ?? "",
                  d.customerDisplayName ?? `#${d.customerId}`,
                  (Number(d.devisTotal) || 0).toFixed(2),
                  (Number(d.facturadoTotal) || 0).toFixed(2),
                  benefice.toFixed(2),
                  d.customerInvoiceStatus,
                  d.technicianInvoice?.invoiceNumberExternal ?? "",
                  d.technicianInvoice?.docStatus ?? "",
                ];
              });
              const stamp = new Date().toISOString().slice(0, 10);
              downloadCsv(`dossiers_${stamp}.csv`, headers, csvRows);
            }}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-3 py-1.5 text-xs font-medium"
          >
            {t("dossiers.exportCsvBtn")}
          </button>
          <button
            onClick={() => void downloadSelectionReport()}
            disabled={selectionReportMutation.isPending}
            title={t("dossiers.reportBtnTitle")}
            className="rounded-md border border-lime/50 bg-lime/10 hover:bg-lime/20 text-lime px-3 py-1.5 text-xs font-medium disabled:opacity-50"
          >
            {selectionReportMutation.isPending
              ? t("dossiers.generating")
              : `📄 ${t("dossiers.reportBtn")}`}
          </button>
        </BulkGroup>
        {/* F-roles — ações de escrita escondidas para visualizador. Export CSV
            (acima) permanece pois é leitura. */}
        {canEdit && (
          <>
            <BulkGroup label={t("dossiers.bulkGroupDocs")}>
              <button
                onClick={() => {
                  // F12.3 §2.2 — abre /proformas/nova com IDs preselecionados no step 2
                  const ids = selection.selectedItems.map((d) => d.id).join(",");
                  setLocation(`/proformas/nova?dossierIds=${ids}`);
                }}
                className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1.5 text-xs"
                title={t("dossiers.generateProformaTitle")}
              >
                {t("dossiers.generateProformaBtn")}
              </button>
              {/* F-consolidated-invoice.6 — emite 1 fatura consolidada cobrindo os
            dossiers selecionados (mesmo cliente). */}
              <button
                onClick={() => {
                  const ids = selection.selectedItems.map((d) => d.id);
                  if (ids.length === 0) return;
                  // CEO 2026-10-28 — abre o RASCUNHO direto (conferência + PDF),
                  // como a ficha do carro; antes passava por /faturas/nova.
                  criarRascunhoLote.mutate({ companyId: companyId ?? 0, dossierIds: ids });
                }}
                disabled={criarRascunhoLote.isPending}
                className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1.5 text-xs disabled:opacity-40"
                title={t("dossiers.issueInvoiceTitle")}
              >
                🧾 {t("dossiers.issueInvoiceBtn")}
              </button>
            </BulkGroup>
            <BulkGroup label={t("dossiers.bulkGroupEdit")}>
              {/* F12.5 — atribuir técnico em massa */}
              <button
                onClick={() => setAssignTechModalOpen(true)}
                className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1.5 text-xs"
                title={t("dossiers.assignTechTitle")}
              >
                {t("dossiers.assignTechBtn")}
              </button>
              {/* F12.5 — atualizar comissão em massa */}
              <button
                onClick={() => setUpdateCommissionModalOpen(true)}
                className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1.5 text-xs"
                title={t("dossiers.updateCommissionTitle")}
              >
                {t("dossiers.updateCommissionBtn")}
              </button>
            </BulkGroup>
            <BulkGroup label={t("dossiers.bulkGroupClient")}>
              {/* F-dossiers-ux — bulk update do status da fatura cliente. */}
              <button
                onClick={async () => {
                  if (
                    !(await modals.confirm({
                      title: t("dossiers.confirmTitle"),
                      message: `${t("dossiers.confirmMarkReceivedPre")} ${selection.count} ${t("dossiers.confirmMarkReceivedSuf")}`,
                    }))
                  )
                    return;
                  bulkUpdateStatusMutation.mutate({
                    companyId: companyId!,
                    dossierIds: selection.selectedItems.map((d) => d.id),
                    newStatus: "paid",
                  });
                }}
                disabled={bulkUpdateStatusMutation.isPending}
                className="rounded-md border border-lime/40 hover:border-lime hover:text-lime text-lime px-3 py-1.5 text-xs disabled:opacity-40"
                title={t("dossiers.markReceivedTitle")}
              >
                ✓ {t("dossiers.markReceivedBtn")}
              </button>
              <button
                onClick={async () => {
                  if (
                    !(await modals.confirm({
                      title: t("dossiers.confirmTitle"),
                      message: `${t("dossiers.confirmRevertIssuedPre")} ${selection.count} ${t("dossiers.confirmRevertIssuedSuf")}`,
                    }))
                  )
                    return;
                  bulkUpdateStatusMutation.mutate({
                    companyId: companyId!,
                    dossierIds: selection.selectedItems.map((d) => d.id),
                    newStatus: "issued",
                  });
                }}
                disabled={bulkUpdateStatusMutation.isPending}
                className="rounded-md border border-sky-500/40 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-600 text-sky-300 px-3 py-1.5 text-xs disabled:opacity-40"
                title={t("dossiers.revertIssuedTitle")}
              >
                ↺ {t("dossiers.revertIssuedBtn")}
              </button>
              {isAdmin && (
                <button
                  onClick={async () => {
                    const reason = await modals.promptReason({
                      title: `${t("dossiers.cancelBtn")} ${selection.count} dossier(s)`,
                      message: t("dossiers.cancelStatusMessage"),
                      placeholder: t("dossiers.cancelReasonPlaceholder"),
                      confirmLabel: t("dossiers.cancelStatusConfirmLabel"),
                      tone: "danger",
                      required: true,
                    });
                    if (!reason) return;
                    bulkUpdateStatusMutation.mutate({
                      companyId: companyId!,
                      dossierIds: selection.selectedItems.map((d) => d.id),
                      newStatus: "cancelled",
                      reason,
                    });
                  }}
                  disabled={bulkUpdateStatusMutation.isPending}
                  className="rounded-md border border-red-500/40 hover:border-red-500 hover:text-red-300 text-red-300 px-3 py-1.5 text-xs disabled:opacity-40"
                  title={t("dossiers.cancelStatusTitle")}
                >
                  ✕ {t("dossiers.cancelBtn")}
                </button>
              )}
            </BulkGroup>
            {/* F-bulk-tech-paid — pagamento ao técnico em lote (pedido do CEO
                2026-08-17). Mesmo sinal do badge "Téc ·" e do KPI "Técnicos a
                pagar": technician_payment_status das service_lines. */}
            <BulkGroup label={t("dossiers.bulkGroupTech")}>
              <button
                onClick={async () => {
                  if (
                    !(await modals.confirm({
                      title: t("dossiers.confirmTitle"),
                      message: `${t("dossiers.confirmTechPaidPre")} ${selection.count} ${t("dossiers.confirmTechPaidSuf")}`,
                    }))
                  )
                    return;
                  bulkTechPayMutation.mutate({
                    companyId: companyId!,
                    dossierIds: selection.selectedItems.map((d) => d.id),
                    newStatus: "paid",
                  });
                }}
                disabled={bulkTechPayMutation.isPending}
                className="rounded-md border border-lime/40 hover:border-lime hover:text-lime text-lime px-3 py-1.5 text-xs disabled:opacity-40"
                title={t("dossiers.markTechPaidTitle")}
              >
                ✓ {t("dossiers.markTechPaidBtn")}
              </button>
              <button
                onClick={async () => {
                  if (
                    !(await modals.confirm({
                      title: t("dossiers.confirmTitle"),
                      message: `${t("dossiers.confirmTechPendingPre")} ${selection.count} ${t("dossiers.confirmTechPendingSuf")}`,
                    }))
                  )
                    return;
                  bulkTechPayMutation.mutate({
                    companyId: companyId!,
                    dossierIds: selection.selectedItems.map((d) => d.id),
                    newStatus: "pending",
                  });
                }}
                disabled={bulkTechPayMutation.isPending}
                className="rounded-md border border-sky-500/40 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-600 text-sky-300 px-3 py-1.5 text-xs disabled:opacity-40"
                title={t("dossiers.revertTechPendingTitle")}
              >
                ↺ {t("dossiers.revertTechPendingBtn")}
              </button>
            </BulkGroup>
          </>
        )}
      </BulkActionBar>

      {/* F12.5 — Modal: atribuir técnico em massa */}
      {canEdit && assignTechModalOpen && (
        <BulkAssignTechnicianModal
          dossierCount={selection.count}
          technicians={techniciansQuery.data?.rows ?? []}
          pending={bulkAssignTechMutation.isPending}
          onClose={() => setAssignTechModalOpen(false)}
          onSubmit={(technicianId, reason) =>
            bulkAssignTechMutation.mutate({
              companyId: companyId!,
              dossierIds: selection.selectedItems.map((d) => d.id),
              technicianId,
              reason: reason || undefined,
            })
          }
        />
      )}

      {/* F12.5 — Modal: atualizar comissão em massa */}
      {canEdit && updateCommissionModalOpen && (
        <BulkUpdateCommissionModal
          dossierCount={selection.count}
          pending={bulkUpdateCommissionMutation.isPending}
          onClose={() => setUpdateCommissionModalOpen(false)}
          onSubmit={(payload) =>
            bulkUpdateCommissionMutation.mutate({
              companyId: companyId!,
              dossierIds: selection.selectedItems.map((d) => d.id),
              ...payload,
            })
          }
        />
      )}

      {fichaVeiculo != null && companyId != null && (
        <VehicleHistoryModal
          companyId={companyId}
          vehicleId={fichaVeiculo}
          onClose={() => setFichaVeiculo(null)}
        />
      )}
      {/* F-comm-cleanup C6 — Modal admin: promover linhas legacy em massa */}
      {canEdit && showPromoteLegacyModal && (
        <PromoteLegacyModal
          companyId={companyId!}
          onClose={() => setShowPromoteLegacyModal(false)}
          onDone={() => {
            setShowPromoteLegacyModal(false);
            utils.dossiers.invalidate();
            utils.serviceLines.invalidate();
          }}
        />
      )}
      {loteDraftId !== null && companyId !== null && (
        <InvoiceDraftModal
          companyId={companyId}
          draftId={loteDraftId}
          onClose={() => {
            setLoteDraftId(null);
            void utils.dossiers.invalidate();
          }}
          onIssued={() => void utils.dossiers.invalidate()}
        />
      )}
    </Shell>
  );
}

// ─── F12.5 — Modais bulk ──────────────────────────────────────────────────────

interface BulkAssignTechnicianModalProps {
  dossierCount: number;
  technicians: RouterOutputs["technicians"]["list"]["rows"];
  pending: boolean;
  onClose: () => void;
  onSubmit: (technicianId: number, reason: string) => void;
}

function BulkAssignTechnicianModal({
  dossierCount,
  technicians,
  pending,
  onClose,
  onSubmit,
}: BulkAssignTechnicianModalProps) {
  const t = useT();
  const [technicianId, setTechnicianId] = useState<number | "">("");
  const [reason, setReason] = useState("");
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (technicianId === "") return;
    onSubmit(Number(technicianId), reason.trim());
  }
  return (
    <BulkModalShell
      title={`${t("dossiers.assignTechModalTitlePre")} ${dossierCount} dossier(s)`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs text-neutral-400 mb-1">
            {t("dossiers.technicianLabel")} <span className="text-red-400">*</span>
          </label>
          <select
            value={technicianId}
            onChange={(e) => setTechnicianId(e.target.value ? Number(e.target.value) : "")}
            required
            disabled={pending}
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
          >
            <option value="">{t("dossiers.optChooseTechnician")}</option>
            {technicians.map((tech) => (
              <option key={tech.id} value={tech.id}>
                {tech.name}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-text-muted mt-1">
            {t("dossiers.assignTechHintPre")} <strong>{t("dossiers.assignTechHintBold")}</strong>{" "}
            {t("dossiers.assignTechHintSuf")}
          </p>
        </div>
        <div>
          <label className="block text-xs text-neutral-400 mb-1">
            {t("dossiers.reasonOptionalLabel")}
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder={t("dossiers.assignTechReasonPlaceholder")}
            maxLength={500}
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("dossiers.cancelBtn")}
          </button>
          <button
            type="submit"
            disabled={pending || technicianId === ""}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {pending ? t("dossiers.applying") : t("dossiers.assignBtn")}
          </button>
        </div>
      </form>
    </BulkModalShell>
  );
}

interface BulkUpdateCommissionModalProps {
  dossierCount: number;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    clientPct?: number;
    technicianPct?: number;
    intermediaryPct?: number;
    reason?: string;
  }) => void;
}

function BulkUpdateCommissionModal({
  dossierCount,
  pending,
  onClose,
  onSubmit,
}: BulkUpdateCommissionModalProps) {
  const t = useT();
  const [clientPct, setClientPct] = useState("");
  const [technicianPct, setTechnicianPct] = useState("");
  const [intermediaryPct, setIntermediaryPct] = useState("");
  const [reason, setReason] = useState("");

  const cN = clientPct ? Number(clientPct) : undefined;
  const tN = technicianPct ? Number(technicianPct) : undefined;
  const iN = intermediaryPct ? Number(intermediaryPct) : undefined;
  const sum = (cN ?? 0) + (tN ?? 0) + (iN ?? 0);
  const eurostorm = Math.max(0, 100 - sum);
  const anyFilled = cN !== undefined || tN !== undefined || iN !== undefined;
  const valid = anyFilled && sum <= 100;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      clientPct: cN,
      technicianPct: tN,
      intermediaryPct: iN,
      reason: reason.trim() || undefined,
    });
  }

  return (
    <BulkModalShell
      title={`${t("dossiers.updateCommissionModalTitlePre")} ${dossierCount} dossier(s)`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-text-muted">
          {t("dossiers.commHintPre")} <strong>complementary</strong> {t("dossiers.commHintMid")}{" "}
          <strong>Euro Storm</strong> {t("dossiers.commHintResidual")} (
          <code>{t("dossiers.commHintFormula")}</code>). {t("dossiers.commHintSuf")}
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("dossiers.pctCustomer")}
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={clientPct}
              onChange={(e) => setClientPct(e.target.value)}
              disabled={pending}
              placeholder={t("dossiers.pctCustomerPlaceholder")}
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("dossiers.pctTechnician")}
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={technicianPct}
              onChange={(e) => setTechnicianPct(e.target.value)}
              disabled={pending}
              placeholder={t("dossiers.pctTechnicianPlaceholder")}
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              {t("dossiers.pctIntermediary")}
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              max={100}
              value={intermediaryPct}
              onChange={(e) => setIntermediaryPct(e.target.value)}
              disabled={pending}
              placeholder={t("dossiers.pctIntermediaryPlaceholder")}
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
            />
          </div>
        </div>
        <div
          className={`rounded-md border p-2 text-xs ${
            sum > 100
              ? "border-red-500/40 bg-red-500/10 text-red-300"
              : "border-neutral-800 bg-neutral-900/30 text-text-muted"
          }`}
        >
          {t("dossiers.sumLabel")} <strong>{sum.toFixed(2)}%</strong> →{" "}
          <span className={sum > 100 ? "text-red-300" : "text-lime"}>
            Euro Storm {t("dossiers.residualWord")} {eurostorm.toFixed(2)}%
          </span>
          {sum > 100 && ` — ${t("dossiers.sumTooHigh")}`}
        </div>
        <div>
          <label className="block text-xs text-neutral-400 mb-1">
            {t("dossiers.reasonOptionalLabel")}
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            placeholder={t("dossiers.commissionReasonPlaceholder")}
            maxLength={500}
            className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
          >
            {t("dossiers.cancelBtn")}
          </button>
          <button
            type="submit"
            disabled={pending || !valid}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
          >
            {pending ? t("dossiers.applying") : t("dossiers.updateBtn")}
          </button>
        </div>
      </form>
    </BulkModalShell>
  );
}

function BulkModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const t = useT();
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
            aria-label={t("dossiers.close")}
          >
            ×
          </button>
        </div>
        <div className="px-5 py-5">{children}</div>
      </div>
    </div>
  );
}

// Campos do painel de filtros: estilo único em FilterBar.tsx.
const inputClass = FILTER_INPUT;

/**
 * F-bulk-tech-paid — grupo rotulado dentro da barra de ações em massa.
 * Organiza os botões por tema (Exportar / Documentos / Edição / Fatura
 * cliente / Pag. técnico) em vez do amontoado único de antes.
 */

// Redesign fase 3 — colunas que o usuário pode esconder pelo menu "Colonnes".
// As estruturais (checkbox, Data/dossier, Véhicule, Ações) ficam sempre visíveis.
const TOGGLEABLE_COLS = [
  { id: "technician", labelKey: "dossiers.colTechnician" },
  { id: "customer", labelKey: "dossiers.colCustomer" },
  { id: "valeurs", labelKey: "dossiers.colValeurs" },
  { id: "repartition", labelKey: "dossiers.colRepartition" },
  { id: "statut", labelKey: "dossiers.colStatut" },
] as const;

/**
 * F13.5 commit 3 — Extrai a chave de ordenação client-side da row da listagem.
 * Os valores monetários vêm como string (`numeric` do Postgres via drizzle), por
 * isso convertemos para Number aqui. `null` → ordenação manda para o fim.
 */
function sortValue(d: DossierRowData, key: SortKey): number | string | null {
  switch (key) {
    case "invoiceDate":
      return dataDaLista(d)?.date ?? null;
    case "plate":
      return d.plate ?? d.vin ?? null;
    case "customer":
      return d.customerDisplayName ?? null;
    case "facturado":
      return toNumber(d.facturadoTotal) ?? null;
    case "benefice":
      return computeBenefice({
        facturado: d.facturadoTotal,
        clientPct: d.primaryLineClientPctF8,
        techPct: d.primaryLineTechnicianPctF8,
        intermPct: d.primaryLineIntermediaryPctF8,
        eurostormPct: d.primaryLineEurostormPctF8,
        legacyTechAmount: d.primaryLineTechnicianCommissionAmount,
        commissionModel: d.primaryLineCommissionModel,
      });
  }
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onClick,
  className = "",
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onClick: (key: SortKey) => void;
  className?: string;
}) {
  const t = useT();
  const isActive = active === sortKey;
  const arrow = isActive ? (dir === "asc" ? "▲" : "▼") : "";
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-2 py-2 cursor-pointer select-none hover:text-text-secondary transition-colors ${className}`}
      title={`${t("dossiers.sortByTitlePre")} ${label}`}
    >
      <span className={isActive ? "text-lime" : ""}>{label}</span>
      {arrow && <span className="ml-1 text-lime text-[8px]">{arrow}</span>}
    </th>
  );
}

/**
 * F13.5 commit 3 — Badge de status para colunas 13/14/15. Não-clicável.
 * Quando `tone` ausente, usa cinza neutro.
 */
/**
 * CEO 2026-10-26 (GK-294-GV, modelo eDSP) — UMA linha por veículo, com um card
 * SÓLIDO por dossier, do mais novo ao mais antigo. O card do dossier a que a
 * linha se refere (data, técnico, valores, ação) leva o contorno. Clicar num
 * card abre aquele dossier. Fatura cancelada / excluído: o selo de antes.
 */
function CardsDoVeiculo({ d, sit }: { d: DossierRowData; sit: SituacaoCarro }) {
  const t = useT();
  // CEO 2026-10-28 — um cartão por REPARO do veículo (espelhos da BS do mesmo
  // dossier da ES, um por técnico, viram um cartão só).
  const cards = d.vehicleDossiers ?? [];
  const especial = d.deletedAt != null || d.customerInvoiceStatus === "cancelled";
  const temAtual = cards.some((c) => c.ids.includes(d.id));
  return (
    <div className="flex flex-col items-start gap-1" data-testid={`dossier-cards-${d.id}`}>
      <div className="flex flex-wrap items-center gap-1">
        {(especial || !temAtual) && <StatusBadge label={sit.label} tone={sit.tone} />}
        {cards.map((c) => {
          const atual = c.ids.includes(d.id);
          if (especial && atual) return null;
          const st = VEHICLE_STATUS_STYLE[c.status] ?? VEHICLE_STATUS_STYLE.cadastro!;
          const parcial = c.partes > 1 && c.faturadas > 0 && c.faturadas < c.partes;
          const titulo =
            c.partes > 1
              ? c.detalhes
                  .map(
                    (p) =>
                      `${p.code}${p.tecnicos ? ` · ${p.tecnicos}` : ""}${p.ref ? ` · ${p.ref}` : ""} · ${t(
                        (VEHICLE_STATUS_STYLE[p.status] ?? VEHICLE_STATUS_STYLE.cadastro!)
                          .label as Parameters<typeof t>[0],
                      )}`,
                  )
                  .join("\n")
              : `${c.code} · ${atual ? sit.label : t(st.label as Parameters<typeof t>[0])}`;
          return (
            <Link
              key={c.id}
              href={`/dossiers/${atual ? d.id : c.id}`}
              title={titulo}
              style={{
                background: st.bg,
                color: "#fff",
                boxShadow: atual && cards.length > 1 ? "0 0 0 2px var(--text-strong)" : undefined,
              }}
              className="whitespace-nowrap rounded px-2 py-0.5 text-[11px] font-bold hover:opacity-90"
              data-testid="vehicle-repair-card"
            >
              {t(st.label as Parameters<typeof t>[0])}
              {parcial && (
                <span className="ml-1 font-semibold opacity-90">
                  · {t("dossiers.cardInvoicedPartial")} {c.faturadas}/{c.partes}
                </span>
              )}
            </Link>
          );
        })}
      </div>
      {d.technicianPart && <ParteDoTecnico d={d} />}
      {sit.sub && <div className="text-[11px] text-text-muted">{sit.sub}</div>}
    </div>
  );
}

/** dd/mm/aa a partir de "aaaa-mm-dd". */
function dataCurta(iso: string | null): string {
  if (!iso) return "";
  const [y, m, dd] = iso.slice(0, 10).split("-");
  return dd && m && y ? `${dd}/${m}/${y.slice(2)}` : iso;
}

/**
 * CEO 2026-10-26 — lista filtrada por técnico: a parte DELE no carro vem
 * primeiro (pago / pago antes do cliente, com a proforma; o que falta), para
 * o "recebido · a pagar" de OUTRO técnico não parecer dívida com ele.
 */
function ParteDoTecnico({ d }: { d: DossierRowData }) {
  const t = useT();
  const p = d.technicianPart;
  if (!p) return null;
  const pago = Math.max(0, p.total - p.pending);
  const antesDoCliente =
    p.paidAt != null &&
    (d.customerInvoiceStatus !== "paid" || (p.clientPaidAt != null && p.paidAt < p.clientPaidAt));
  const ref = p.paidRef ? ` · ${p.paidRef}${p.paidAt ? ` · ${dataCurta(p.paidAt)}` : ""}` : "";
  const liberado = d.customerInvoiceStatus === "paid";
  const temPago = p.paid || pago > 0.005;
  // Parte ainda sem valor: "a calcular"; parte de 0 € sem nada pago: nada a mostrar.
  if (!temPago && p.pending <= 0.005 && !p.tbd) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-1 text-[11px]"
      data-testid={`tech-part-${d.id}`}
    >
      <span className="font-semibold text-text-secondary">👤 {p.name}:</span>
      {temPago && (
        <span className={`badge whitespace-nowrap ${antesDoCliente ? "b-violet" : "b-ok"}`}>
          ✓ {antesDoCliente ? t("dossiers.techPartPaidBefore") : t("dossiers.techPartPaid")}
          {!p.paid && ` ${formatMoney(pago, { decimals: 2 })}`}
          {ref}
        </span>
      )}
      {!temPago && p.pending <= 0.005 && p.tbd && (
        <span className="badge b-mut whitespace-nowrap">{t("dossiers.sitPayoutTbd")}</span>
      )}
      {p.pending > 0.005 && (
        <span className={`badge whitespace-nowrap ${liberado ? "b-warn" : "b-mut"}`}>
          {pago > 0.005 ? t("dossiers.techPartMissing") : t("dossiers.techPartToReceive")}{" "}
          {formatMoney(p.pending, { decimals: 2 })} ·{" "}
          {liberado ? t("dossiers.techPartReleased") : t("dossiers.techPartAwaiting")}
        </span>
      )}
    </div>
  );
}

function StatusBadge({ label, tone }: { label: string; tone?: string }) {
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
        tone ?? "bg-neutral-700/40 text-text-secondary"
      }`}
    >
      {label}
    </span>
  );
}

const dossierRowEqual = (a: DossierRowProps, b: DossierRowProps) =>
  a.selected === b.selected &&
  a.isAdmin === b.isAdmin &&
  a.restorePending === b.restorePending &&
  a.deletePending === b.deletePending &&
  a.companyName === b.companyName &&
  a.hiddenCols === b.hiddenCols &&
  a.layout === b.layout &&
  a.d === b.d;

interface DossierRowProps {
  d: DossierRowData;
  selected: boolean;
  onToggleSelect: () => void;
  onSoftDelete: () => void;
  onRestore: () => void;
  isAdmin: boolean;
  restorePending: boolean;
  deletePending: boolean;
  /** F-billing-company-label — nome da empresa ativa (absorve o resíduo na cascade). */
  companyName: string;
  /** Redesign fase 3 — ids de TOGGLEABLE_COLS escondidos pelo menu "Colonnes".
      Comparado por identidade no memo (o Set só troca quando o usuário mexe). */
  hiddenCols: ReadonlySet<string>;
  /** F-listas-cartoes — "card" no celular (< 768 px); "row" = linha da tabela. */
  layout?: "row" | "card";
  /** CEO 2026-10-26 — abre a ficha do veículo (quando ele tem mais de um dossier). */
  onOpenVehicle?: (vehicleId: number) => void;
}

/**
 * F13.5 commit 3 — Row do /dossiers. Memoizado pois quando muda 1 row
 * (re-fetch parcial / sort), as outras 49 não precisam re-renderizar.
 * A igualdade compara `d` por identidade — a lista vem inteira de um
 * useQuery, então rows não mudam de identidade entre re-fetches sem dado novo.
 */
const DossierRow = memo(function DossierRow({
  d,
  selected,
  onToggleSelect,
  onSoftDelete,
  onRestore,
  isAdmin,
  restorePending,
  deletePending,
  companyName,
  hiddenCols,
  layout = "row",
  onOpenVehicle,
}: DossierRowProps) {
  const t = useT();
  const [, setLocation] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // F-row-menu-fixed (CEO 2026-09-16: "não consigo clicar no ⋮") — o dropdown
  // era `absolute` DENTRO do wrapper com overflow da tabela: com poucas linhas
  // ele abria cortado (invisível) e parecia que o clique não funcionava.
  // Agora abre ancorado à janela (fixed), fora de qualquer recorte.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const deleted = d.deletedAt != null;
  const plate = d.plate || d.vin || "—";
  // Memoizados por locale: DossierRow é memo() e renderiza até 50x por página.
  const { grantOnly: rowGrantOnly } = useGrantOnly();
  const sit = situacaoDoCarro(d, t, rowGrantOnly);

  // F-listas-cartoes — botão ⋮ e menu são os mesmos na linha e no cartão.
  const menuButton = (
    <button
      type="button"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setMenuPos({
          top: r.bottom + 4,
          right: Math.max(8, window.innerWidth - r.right),
        });
        setMenuOpen((v) => !v);
      }}
      title={t("dossiers.colActions")}
      className={`inline-flex ${layout === "card" ? "h-10 w-10 text-base" : "h-6 w-6"} items-center justify-center rounded-md border transition-colors ${
        menuOpen
          ? "border-lime text-lime"
          : "border-neutral-800 text-text-secondary hover:border-lime hover:text-lime"
      }`}
    >
      ⋮
    </button>
  );
  const menuEl = menuOpen && menuPos && (
    <>
      {/* overlay invisível: clique fora fecha o menu */}
      <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
      <div
        className="fixed z-20 min-w-[230px] rounded-lg border border-neutral-700 bg-neutral-950 p-1 text-left shadow-xl"
        style={{ top: menuPos.top, right: menuPos.right }}
      >
        {deleted ? (
          isAdmin && (
            <button
              onClick={() => {
                setMenuOpen(false);
                onRestore();
              }}
              disabled={restorePending}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-amber-300 hover:bg-amber-500/10"
            >
              ↩️ {t("dossiers.restoreBtn")}
              <span className="ml-auto text-[10px] text-text-muted">
                {t("dossiers.menuRestoreHint")}
              </span>
            </button>
          )
        ) : (
          <>
            <Link
              href={`/dossiers/${d.id}`}
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-text-primary hover:bg-neutral-800"
            >
              ✏️ {t("dossiers.menuModify")}
            </Link>
            <Link
              href={`/dossiers/${d.id}`}
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-text-primary hover:bg-neutral-800"
            >
              🕘 {t("dossiers.menuHistory")}
            </Link>
            <Link
              href={`/dossiers/${d.id}`}
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-text-primary hover:bg-neutral-800"
            >
              📎 {t("dossiers.menuAttach")}
            </Link>
            {isAdmin && (
              <>
                <div className="my-1 h-px bg-neutral-800" />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onSoftDelete();
                  }}
                  disabled={deletePending}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-xs text-red-300 hover:bg-red-500/10"
                >
                  🗑 {t("dossiers.deleteBtn")}
                  <span className="ml-auto text-[10px] text-text-muted">
                    {t("dossiers.menuDeleteHint")}
                  </span>
                </button>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
  const abrirDossier = (e: React.MouseEvent) => {
    const alvo = e.target as HTMLElement;
    if (alvo.closest("a, button, input, label, select, [data-no-row-nav]")) return;
    setLocation(`/dossiers/${d.id}`);
  };

  if (layout === "card") {
    const dt = dataDaLista(d);
    const ref = d.customerInvoiceNumber ?? d.invoiceNumberExternal;
    return (
      <div
        onClick={abrirDossier}
        data-testid="dossier-card"
        data-dossier-code={d.dossierCode}
        className={`${deleted ? "opacity-50 line-through" : ""} ${selected ? "bg-lime/5" : ""} flex cursor-pointer gap-3 px-3 py-3 active:bg-neutral-800/30`}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={d.dossierCode}
          className="mt-1 h-5 w-5 shrink-0 accent-lime"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <CarLogo make={d.vehicleMakeSnapshot} size={30} />
            <div className="min-w-0">
              <div className="whitespace-nowrap font-mono text-base font-bold leading-tight text-text-primary">
                {plate}
                {d.vehicleId != null && d.vehicleDossierCount > 1 && onOpenVehicle && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenVehicle(d.vehicleId!);
                    }}
                    title={t("dossiers.vehicleDossiersHint")}
                    className="badge b-warn ml-1 cursor-pointer px-1 text-[10px] font-semibold hover:opacity-80"
                    data-testid={`dossier-vehicle-count-${d.id}`}
                  >
                    🚗 {d.vehicleDossierCount}
                  </button>
                )}
              </div>
              <div className="truncate text-xs text-text-secondary">
                {[d.vehicleMakeSnapshot, d.vehicleModelSnapshot].filter(Boolean).join(" ") || "—"}
              </div>
            </div>
          </div>
          <div className="mt-1.5">
            <CardsDoVeiculo d={d} sit={sit} />
          </div>
          <div className="mt-2 break-words text-sm text-text-primary">
            {d.customerDisplayName ?? `#${d.customerId}`}
            {d.source === "proforma_mirror" && d.externalDevisRef && (
              <span
                className="ml-1 rounded bg-sky-500/15 px-1.5 py-0.5 font-mono text-[10px] text-sky-300"
                title={d.externalDevisRef}
                data-testid="proforma-chip"
              >
                {d.externalDevisRef.split("#")[0]}
              </span>
            )}
            {d.endCustomerName && <span className="text-text-muted"> ↳ {d.endCustomerName}</span>}
            {d.atelierName && <span className="text-text-muted"> · 🏭 {d.atelierName}</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
            <span className="text-text-secondary">
              🔧 {d.technicianName ?? <span className="text-amber-400">⚠ —</span>}
            </span>
            <span className="whitespace-nowrap tabular-nums">
              <span className="text-text-muted">{t("dossiers.valDevisShort")}</span>{" "}
              <b className="font-display">{formatMoney(d.devisTotal, { decimals: 0 })}</b> ·{" "}
              <span className="text-text-muted">{t("dossiers.valFactShort")}</span>{" "}
              <b className={`font-display ${d.customerInvoiceNumber ? "text-lime" : ""}`}>
                {formatMoney(d.facturadoTotal, { decimals: 0 })}
              </b>
            </span>
          </div>
          {sit.acao?.primario && (
            <Link
              href={sit.acao.href}
              className="mt-2 inline-flex h-10 w-full items-center justify-center rounded-md bg-lime text-sm font-semibold text-on-lime"
              data-testid={`card-action-${d.id}`}
            >
              {sit.acao.label}
            </Link>
          )}
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="min-w-0 break-words text-[11px] tabular-nums text-text-muted">
              <span className={ref ? "font-mono font-semibold text-lime" : "font-mono"}>
                {ref ?? d.dossierCode}
              </span>
              {dt && (
                <>
                  {" "}
                  · {dt.date}
                  {dt.kind !== "invoice" && (
                    <span className="opacity-60">
                      {" "}
                      {dt.kind === "repaired"
                        ? "(réparé)"
                        : dt.kind === "devis"
                          ? "(devis)"
                          : "(créé)"}
                    </span>
                  )}
                </>
              )}
            </span>
            <div className="relative flex shrink-0 gap-1">
              <Link
                href={`/dossiers/${d.id}`}
                title={t("dossiers.menuAttach")}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-neutral-800 text-base text-text-secondary"
              >
                📎
              </Link>
              {menuButton}
              {menuEl}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr
      // CEO 2026-09-24: dossier ainda sem fatura (o caso "a faturar") não tinha
      // link nenhum na primeira coluna — só "—" — e o 📎 ficava fora da tela no
      // scroll horizontal. Clicar na linha "não fazia nada". A linha inteira
      // abre o dossier; checkbox, links, botões e o menu ⋮ continuam os deles.
      onClick={(e) => {
        const alvo = e.target as HTMLElement;
        if (alvo.closest("a, button, input, label, select, [data-no-row-nav]")) return;
        setLocation(`/dossiers/${d.id}`);
      }}
      className={`${deleted ? "opacity-50 line-through" : ""} ${selected ? "bg-lime/5" : ""} cursor-pointer hover:bg-neutral-800/30 transition-colors`}
    >
      {/* 1. Checkbox */}
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="accent-lime"
        />
      </td>
      {/* 2. Date / dossier (ref lime + data, empilhados) */}
      <td className="px-2 py-2 leading-tight" data-dossier-code={d.dossierCode}>
        {(d.customerInvoiceNumber ?? d.invoiceNumberExternal) ? (
          <Link
            href={`/dossiers/${d.id}`}
            className="text-lime text-xs font-mono font-semibold underline decoration-transparent underline-offset-2 hover:decoration-lime-bright hover:text-lime-bright transition-colors"
          >
            {d.customerInvoiceNumber ?? d.invoiceNumberExternal}
          </Link>
        ) : (
          // Sem fatura ainda: o código do dossier vira o link, em vez de "—".
          <Link
            href={`/dossiers/${d.id}`}
            className="text-text-muted text-xs font-mono underline decoration-transparent underline-offset-2 hover:decoration-lime-bright hover:text-lime-bright transition-colors"
          >
            {d.dossierCode}
          </Link>
        )}
        {(() => {
          const dt = dataDaLista(d);
          return (
            <div
              className="text-[11px] text-text-muted tabular-nums"
              title={
                dt
                  ? t(
                      dt.kind === "invoice"
                        ? "dossiers.dateKindInvoice"
                        : dt.kind === "repaired"
                          ? "dossiers.dateKindRepaired"
                          : dt.kind === "devis"
                            ? "dossiers.dateKindDevis"
                            : "dossiers.dateKindCreated",
                    )
                  : undefined
              }
            >
              {dt ? dt.date : "—"}
              {dt && dt.kind !== "invoice" && (
                <span className="ml-1 opacity-60">
                  {dt.kind === "repaired" ? "(réparé)" : dt.kind === "devis" ? "(devis)" : "(créé)"}
                </span>
              )}
            </div>
          );
        })()}
      </td>
      {/* 3. Véhicule (logo da marca + marca+modelo em cima, placa mono embaixo) */}
      <td className="px-2 py-2 leading-tight max-w-[170px]">
        <div className="flex items-center gap-1.5">
          <CarLogo make={d.vehicleMakeSnapshot} size={26} />
          <div className="min-w-0">
            <div
              className="text-xs font-semibold text-text-primary truncate"
              title={
                `${d.vehicleMakeSnapshot ?? ""} ${d.vehicleModelSnapshot ?? ""}`.trim() || undefined
              }
            >
              {[d.vehicleMakeSnapshot, d.vehicleModelSnapshot].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="whitespace-nowrap text-[11px] font-mono text-text-secondary">
              {plate}
              {d.vehicleId != null && d.vehicleDossierCount > 1 && onOpenVehicle && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenVehicle(d.vehicleId!);
                  }}
                  title={t("dossiers.vehicleDossiersHint")}
                  className="badge b-warn ml-1 cursor-pointer px-1 text-[10px] font-semibold hover:opacity-80"
                  data-testid={`dossier-vehicle-count-${d.id}`}
                >
                  🚗 {d.vehicleDossierCount}
                </button>
              )}
            </div>
          </div>
        </div>
      </td>
      {/* 4. Technicien */}
      {!hiddenCols.has("technician") && (
        <td
          className="px-2 py-2 text-xs text-text-secondary truncate max-w-[130px]"
          title={d.technicianName ?? undefined}
        >
          {d.technicianName ?? <span className="text-amber-400">⚠ —</span>}
        </td>
      )}
      {/* 5. Client — F-mirror-end-customer: no espelho BS o customer é a Euro
          Storm (faturação); o CLIENTE FINAL da origem aparece embaixo. */}
      {!hiddenCols.has("customer") && (
        <td
          className="px-2 py-2 text-sm max-w-[180px]"
          title={
            [d.customerDisplayName, d.endCustomerName, d.atelierName].filter(Boolean).join(" · ") ||
            undefined
          }
        >
          <div className="truncate">{d.customerDisplayName ?? `#${d.customerId}`}</div>
          {/* F-bs-proforma-filter — chip em linha própria: dentro do truncate ficava cortado. */}
          {d.source === "proforma_mirror" && d.externalDevisRef && (
            <div>
              <span
                className="rounded bg-sky-500/15 px-1.5 py-0.5 font-mono text-[10px] text-sky-300"
                title={d.externalDevisRef}
                data-testid="proforma-chip"
              >
                {d.externalDevisRef.split("#")[0]}
              </span>
            </div>
          )}
          {d.endCustomerName && (
            <div className="truncate text-[11px] text-text-muted">↳ {d.endCustomerName}</div>
          )}
          {/* F-list-atelier — o atelier do dossier (onde o carro está). */}
          {d.atelierName && (
            <div className="truncate text-[11px] text-text-muted">🏭 {d.atelierName}</div>
          )}
        </td>
      )}
      {/* 6. Valeurs (Devis / Facturé empilhados; facturé em lime quando ≥ devis) */}
      {!hiddenCols.has("valeurs") && (
        <td className="px-2 py-2 text-right leading-tight">
          <div className="flex justify-end gap-1.5 text-xs tabular-nums">
            <span className="text-text-muted">{t("dossiers.valDevisShort")}</span>
            <span className="font-display font-bold text-text-primary">
              {formatMoney(d.devisTotal, { decimals: 0 })}
            </span>
          </div>
          <div className="flex justify-end gap-1.5 text-xs tabular-nums">
            <span className="text-text-muted">{t("dossiers.valFactShort")}</span>
            <span
              // F-list-fact-net — verde quando há FATURA emitida (com remise o
              // faturado é sempre < devis, então "≥ devis" nunca acenderia).
              className={`font-display font-bold ${
                d.customerInvoiceNumber ? "text-lime" : "text-text-primary"
              }`}
            >
              {formatMoney(d.facturadoTotal, { decimals: 0 })}
            </span>
          </div>
        </td>
      )}
      {/* 7. Répartition (cascade inalterada) */}
      {!hiddenCols.has("repartition") && (
        <td className="px-2 py-2 align-top">
          <CommissionCascade
            facturado={d.facturadoTotal}
            clientPct={d.primaryLineClientPctF8}
            techPct={d.primaryLineTechnicianPctF8}
            intermPct={d.primaryLineIntermediaryPctF8}
            eurostormPct={d.primaryLineEurostormPctF8}
            clientAmount={d.clientAmountTotal}
            // F-cascade-montage — "Téc" = só o técnico do PDR; o montador (outro
            // técnico) vai na linha "Montage" com a % dele.
            techAmount={
              (Number(d.technicianAmountTotal) || 0) - (Number(d.otherTechLinesAmountTotal) || 0)
            }
            otherTechAmount={d.otherTechAmountTotal}
            otherTechPct={d.otherTechPct}
            otherTechName={d.otherTechNames}
            intermAmount={d.intermediaryAmountTotal}
            eurostormAmount={d.eurostormAmountTotal}
            intermName={d.intermediaryCompanyName}
            commissionModel={d.primaryLineCommissionModel}
            commissionBase={d.primaryLineCommissionBaseF8}
            legacyTechAmount={d.primaryLineTechnicianCommissionAmount}
            companyName={companyName}
          />
        </td>
      )}
      {/* 8. Situação — uma frase (F-lista-carros) */}
      {!hiddenCols.has("statut") && (
        <td className="px-2 py-2">
          <CardsDoVeiculo d={d} sit={sit} />
        </td>
      )}
      {/* 8b. Ação — o próximo passo do escritório, na linha */}
      <td className="whitespace-nowrap px-2 py-2">
        {sit.acao && (
          <Link
            href={sit.acao.href}
            className={
              sit.acao.primario
                ? "inline-flex h-7 items-center rounded-md bg-lime px-2.5 text-xs font-semibold text-on-lime hover:bg-lime-bright"
                : "inline-flex h-7 items-center rounded-md border border-neutral-700 px-2.5 text-xs text-text-secondary transition-colors hover:border-lime hover:text-lime"
            }
            data-testid={`row-action-${d.id}`}
          >
            {sit.acao.label}
          </Link>
        )}
      </td>
      {/* 9. Actions — 📎 (documentos no detalhe) + ⋮ (menu) */}
      <td className="px-2 py-2 text-right">
        <div className="relative flex justify-end gap-1">
          <Link
            href={`/dossiers/${d.id}`}
            title={t("dossiers.menuAttach")}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-neutral-800 text-text-secondary hover:border-lime hover:text-lime transition-colors"
          >
            📎
          </Link>
          {menuButton}
          {menuEl}
        </div>
      </td>
    </tr>
  );
}, dossierRowEqual);

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-screen-2xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}

// ─── F-comm-cleanup C6 — Modal admin: promover linhas legacy em massa ─────────

interface PromoteLegacyModalProps {
  companyId: number;
  onClose: () => void;
  onDone: () => void;
}

function PromoteLegacyModal({ companyId, onClose, onDone }: PromoteLegacyModalProps) {
  const t = useT();
  const [phase, setPhase] = useState<"idle" | "preview" | "result">("idle");
  const [previewData, setPreviewData] = useState<{
    totalLegacy: number;
    promoted: number[];
    skipped: { id: number; reason: string }[];
    errors: { id: number; message: string }[];
  } | null>(null);
  const [resultData, setResultData] = useState<typeof previewData>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = trpc.serviceLines.bulkPromoteLegacy.useMutation({
    onSuccess: (data) => {
      if (data.dryRun) {
        setPreviewData(data);
        setPhase("preview");
      } else {
        setResultData(data);
        setPhase("result");
      }
      setError(null);
    },
    onError: (e) => setError(e.message),
  });

  // F-bulk-recalc-stale + F-migration-fix-historical-faturado (2026-05-27):
  // recalcula 3 cenários:
  //   1. legacy → complementary (defaults atuais)
  //   2. complementary stale (tec=0 E interm=0)
  //   3. complementary broken faturado (faturado=orcamento) — corrige
  //      historicamente o bug do importador eDSP antigo que não aplicava
  //      a retenção do cliente no faturado. Cliente sem retenção não muda.
  function runDryRun() {
    setError(null);
    mutation.mutate({
      companyId,
      dryRun: true,
      includeStaleComplementary: true,
      includeBrokenFaturado: true,
    });
  }

  function runReal() {
    setError(null);
    mutation.mutate({
      companyId,
      dryRun: false,
      includeStaleComplementary: true,
      includeBrokenFaturado: true,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-3xl bg-neutral-950 border border-neutral-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-neutral-800 flex items-center justify-between sticky top-0 bg-neutral-950">
          <div>
            <h2 className="text-lg font-semibold">⚡ {t("dossiers.recalcCommissionsBtn")}</h2>
            <p className="text-xs text-neutral-500 mt-0.5">{t("dossiers.promoteIntro")}</p>
            <ul className="text-xs text-neutral-500 mt-1 ml-3 space-y-0.5 list-disc list-inside">
              <li>
                {t("dossiers.promoteLinesWord")} <code className="text-amber-300">legacy</code> —{" "}
                {t("dossiers.promoteToWord")} <code className="text-lime">complementary</code>{" "}
                {t("dossiers.promoteApplyingV3")}
              </li>
              <li>
                {t("dossiers.promoteLinesWord")}{" "}
                <code className="text-lime">complementary stale</code>{" "}
                {t("dossiers.promoteStaleDesc")} (<code>{t("dossiers.promoteStaleCode")}</code>).
              </li>
              <li>
                {t("dossiers.promoteLinesWord")}{" "}
                <code className="text-lime">{t("dossiers.promoteBrokenCode")}</code>{" "}
                {t("dossiers.promoteBrokenDesc")} <code>defaultRetentionPct &gt; 0</code>.
              </li>
            </ul>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-300 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {phase === "idle" && (
            <>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 space-y-2">
                <p>
                  <strong>{t("dossiers.promoteRecommendedLabel")}</strong>{" "}
                  {t("dossiers.promoteRecommendedPre")} <strong>preview</strong>{" "}
                  {t("dossiers.promoteRecommendedSuf")}
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>{t("dossiers.promoteBullet1")}</li>
                  <li>{t("dossiers.promoteBullet2")}</li>
                  <li>{t("dossiers.promoteBullet3")}</li>
                </ul>
                <p>
                  {t("dossiers.promoteWarnPre")} <code>defaultRetentionPct</code>{" "}
                  {t("dossiers.promoteWarnSuf")}
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
                >
                  {t("dossiers.cancelBtn")}
                </button>
                <button
                  onClick={runDryRun}
                  disabled={mutation.isPending}
                  className="rounded-md border border-amber-500/50 hover:border-amber-500 text-amber-300 hover:bg-amber-500/10 px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                >
                  {mutation.isPending ? t("dossiers.analyzing") : t("dossiers.runDryRunBtn")}
                </button>
              </div>
            </>
          )}

          {phase === "preview" && previewData && (
            <>
              <div className="rounded-md border border-sky-500/30 bg-sky-500/5 p-3 space-y-2 text-sm">
                <p className="text-sky-300 font-medium">📋 {t("dossiers.previewNotApplied")}</p>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promoteTotalLegacy")}</div>
                    <div className="text-lg text-neutral-200 tabular-nums">
                      {previewData.totalLegacy}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promoteWillPromote")}</div>
                    <div className="text-lg text-lime tabular-nums">
                      {previewData.promoted.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promoteWillSkip")}</div>
                    <div className="text-lg text-amber-300 tabular-nums">
                      {previewData.skipped.length}
                    </div>
                  </div>
                </div>
                {previewData.skipped.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-neutral-400 hover:text-neutral-200">
                      {t("dossiers.promoteSeeReasonsPre")} {previewData.skipped.length}{" "}
                      {t("dossiers.promoteSeeReasonsSuf")}
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs text-neutral-400 max-h-48 overflow-y-auto">
                      {previewData.skipped.slice(0, 50).map((s) => (
                        <li key={s.id}>
                          • Line #{s.id}: {s.reason}
                        </li>
                      ))}
                      {previewData.skipped.length > 50 && (
                        <li className="text-neutral-500">
                          … {t("dossiers.promoteAndMorePre")} {previewData.skipped.length - 50}
                        </li>
                      )}
                    </ul>
                  </details>
                )}
                {previewData.errors.length > 0 && (
                  <p className="text-xs text-red-400 mt-2">
                    ⚠ {previewData.errors.length} {t("dossiers.promoteErrorsDuringCalc")}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
                >
                  {t("dossiers.close")}
                </button>
                <button
                  onClick={runReal}
                  disabled={mutation.isPending || previewData.promoted.length === 0}
                  className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
                >
                  {mutation.isPending
                    ? t("dossiers.promoting")
                    : `${t("dossiers.promoteBtnPre")} ${previewData.promoted.length} ${t("dossiers.linesWord")}`}
                </button>
              </div>
            </>
          )}

          {phase === "result" && resultData && (
            <>
              <div className="rounded-md border border-lime/30 bg-lime/5 p-3 space-y-2 text-sm">
                <p className="text-lime font-medium">✅ {t("dossiers.doneLabel")}</p>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promoteTotalLegacyWas")}</div>
                    <div className="text-lg text-neutral-200 tabular-nums">
                      {resultData.totalLegacy}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promotePromoted")}</div>
                    <div className="text-lg text-lime tabular-nums">
                      {resultData.promoted.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-neutral-500">{t("dossiers.promoteRemainedLegacy")}</div>
                    <div className="text-lg text-amber-300 tabular-nums">
                      {resultData.skipped.length}
                    </div>
                  </div>
                </div>
                {resultData.errors.length > 0 && (
                  <p className="text-xs text-red-400 mt-2">
                    ⚠ {resultData.errors.length} {t("dossiers.promoteErrorsLogged")}
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onDone}
                  className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
                >
                  {t("dossiers.okBtn")}
                </button>
              </div>
            </>
          )}

          {error && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * F-dossiers-kpis (2026-05-28) + F-dossiers-kpis-v2 — Painel de KPIs.
 *
 * 6 cards:
 *  1. Dossiers (count, filtrado ou geral)
 *  2. Total devis (soma orçamento, filtrado)
 *  3. Total facturé (soma faturado, filtrado)
 *  4. A receber (soma totalFaturado − recebido em invoices 'issued')
 *  5. Lucro previsto (soma eurostorm_amount, filtrado)
 *  6. Técnicos a pagar (filtrado, clickable) — soma tec+interm das lines
 *     onde cliente já pagou E técnico ainda pending, dentro do filtro atual
 *     (sem filtro = total global da empresa). Clique aplica technicianPaymentPending.
 *
 * Label dos 6 cards muda entre "(Filtrado)" / "(Geral)" conforme hasFilter.
 */
function formatEuro(v: number): string {
  return `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v)} €`;
}

function KpiPanel({
  loading,
  hasFilter,
  dossierCount,
  orcamentoTotal,
  facturadoTotal,
  facturadoNetTotal,
  avoirsCaTotal,
  avoirsLossTotal,
  lucroPrevisto,
  lucroBruto,
  comissoesTotal,
  aReceberCliente,
  technicianAmountToPay,
  technicianDossiersToPayCount,
  technicianFilterActive,
  onToggleTechnicianFilter,
}: {
  loading: boolean;
  hasFilter: boolean;
  dossierCount: number;
  orcamentoTotal: string;
  facturadoTotal: string;
  facturadoNetTotal: string;
  avoirsCaTotal: string;
  /** CEO 2026-10-27 — irrecuperáveis também saem do Lucro bruto. */
  avoirsLossTotal: string;
  lucroPrevisto: string;
  lucroBruto: string;
  comissoesTotal: string;
  aReceberCliente: string;
  technicianAmountToPay: string;
  technicianDossiersToPayCount: number;
  technicianFilterActive: boolean;
  onToggleTechnicianFilter: () => void;
}) {
  const t = useT();
  const scopeLabel = hasFilter ? t("dossiers.kpiScopeFiltered") : t("dossiers.kpiScopeGeneral");
  const orcamentoNum = Number(orcamentoTotal) || 0;
  const facturadoNum = Number(facturadoTotal) || 0;
  const avoirCaNum = Number(avoirsCaTotal) || 0;
  const facturadoNetNum = Number(facturadoNetTotal) || 0;
  const lucroNum = Number(lucroPrevisto) || 0;
  const lucroBrutoNum = Number(lucroBruto) || 0;
  const comissoesNum = Number(comissoesTotal) || 0;
  const aReceberNum = Number(aReceberCliente) || 0;
  const toPayNum = Number(technicianAmountToPay) || 0;
  // Redesign fase 2 — percentuais derivados nos subtítulos (mockup aprovado):
  // facturé como % do devis; lucro bruto como % do facturé.
  const facturadoEfetivo = avoirCaNum > 0 ? facturadoNetNum : facturadoNum;
  const formatPct = (v: number) =>
    new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
      v,
    );
  const pctFacture =
    orcamentoNum > 0 ? `${formatPct((facturadoEfetivo / orcamentoNum) * 100)}%` : null;
  const pctLucroBruto =
    facturadoEfetivo > 0 ? `${formatPct((lucroBrutoNum / facturadoEfetivo) * 100)}%` : null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
      <KpiCard
        label={`${t("dossiers.kpiDossiers")} (${scopeLabel})`}
        value={loading ? "—" : String(dossierCount)}
        tone="neutral"
      />
      <KpiCard
        label={`${t("dossiers.kpiTotalDevis")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(orcamentoNum)}
        tone="neutral"
      />
      <KpiCard
        label={`${t("dossiers.kpiTotalFacture")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(facturadoEfetivo)}
        subtitle={
          loading
            ? undefined
            : [
                pctFacture ? `${pctFacture} ${t("dossiers.kpiPctOfDevisSuf")}` : null,
                avoirCaNum > 0
                  ? `${t("dossiers.kpiGrossWord")} ${formatEuro(facturadoNum)} · −${formatEuro(avoirCaNum)} ${t("dossiers.kpiAvoirsWord")}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
        }
        tone="lime"
      />
      <KpiCard
        label={`${t("dossiers.kpiLucroBruto")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(lucroBrutoNum)}
        subtitle={
          loading
            ? undefined
            : comissoesNum === 0 && lucroBrutoNum > 0
              ? `⚠ ${t("dossiers.kpiCommissionsNotComputed")}`
              : [
                  pctLucroBruto ? `${pctLucroBruto} ${t("dossiers.kpiPctOfFactureSuf")}` : null,
                  `${t("dossiers.kpiFaturadoMinusCommissions")} (${formatEuro(comissoesNum)})`,
                  // CEO 2026-10-27 — só a casa absorve o avoir.
                  avoirCaNum + (Number(avoirsLossTotal) || 0) > 0.005
                    ? `− ${t("dossiers.kpiAvoirsWord")} (${formatEuro(avoirCaNum + (Number(avoirsLossTotal) || 0))})`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")
        }
        tone={comissoesNum === 0 && lucroBrutoNum > 0 ? "amber" : "lime"}
      />
      <KpiCard
        label={`${t("dossiers.kpiAReceber")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(aReceberNum)}
        subtitle={loading ? undefined : t("dossiers.kpiIssuedPending")}
        tone={aReceberNum > 0 ? "sky" : "neutral"}
      />
      <KpiCard
        label={`${t("dossiers.kpiLucroPrevisto")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(lucroNum)}
        subtitle={
          loading
            ? undefined
            : avoirCaNum + (Number(avoirsLossTotal) || 0) > 0.005
              ? `${t("dossiers.kpiEurostormShare")} · − ${t("dossiers.kpiAvoirsWord")} (${formatEuro(avoirCaNum + (Number(avoirsLossTotal) || 0))})`
              : t("dossiers.kpiEurostormShare")
        }
        tone="lime"
      />
      <KpiCard
        label={`${t("dossiers.kpiTechniciansToPay")} (${scopeLabel})`}
        value={loading ? "—" : formatEuro(toPayNum)}
        subtitle={
          loading
            ? undefined
            : technicianFilterActive
              ? `✓ ${t("dossiers.kpiFilterAppliedClickRemove")}`
              : toPayNum > 0
                ? `${technicianDossiersToPayCount} dossier${technicianDossiersToPayCount === 1 ? "" : "s"} — ${t("dossiers.kpiClickToFilter")}`
                : t("dossiers.kpiAllPaid")
        }
        tone={technicianFilterActive ? "amber-active" : toPayNum > 0 ? "amber" : "neutral"}
        // Só clicável quando há o que filtrar (> 0) ou quando o filtro já está
        // ligado (pra poder desligar). Com 0,00 e inativo o clique só esvaziaria
        // a tela — vira card comum. (Evita o "zerou tudo" do filtro cruzado.)
        onClick={technicianFilterActive || toPayNum > 0 ? onToggleTechnicianFilter : undefined}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone: "neutral" | "lime" | "amber" | "amber-active" | "sky";
  onClick?: () => void;
}) {
  const t = useT();
  // Redesign V4 — card no estilo .kpi do shell (branco, label Saira, valor
  // grande, tile de ícone tonalizado). tone → ícone + cores (tokens theme-aware).
  const TONE: Record<string, { icon: typeof Filter; bg: string; fg: string; val: string }> = {
    neutral: {
      icon: FolderOpen,
      bg: "var(--surface-muted)",
      fg: "var(--text-muted)",
      val: "var(--text-strong)",
    },
    lime: {
      icon: TrendingUp,
      bg: "var(--accent-soft)",
      fg: "var(--accent)",
      val: "var(--accent-press)",
    },
    sky: { icon: Clock, bg: "var(--info-50)", fg: "var(--info-500)", val: "var(--info-500)" },
    amber: {
      icon: Wrench,
      bg: "var(--warning-50)",
      fg: "var(--warning-500)",
      val: "var(--warning-500)",
    },
    "amber-active": {
      icon: Wrench,
      bg: "var(--warning-50)",
      fg: "var(--warning-500)",
      val: "var(--warning-500)",
    },
  };
  const tk = TONE[tone] ?? TONE.neutral!;
  const Icon = tk.icon;
  const activeBorder = tone === "amber-active" ? { borderColor: "var(--accent)" } : undefined;
  const inner = (
    <>
      <div className="kpi__top">
        <span className="kpi__lab">{label}</span>
        <span className="kpi__ico" style={{ background: tk.bg, color: tk.fg }}>
          {onClick ? <Filter size={16} /> : <Icon size={16} />}
        </span>
      </div>
      <div className="kpi__v" style={{ color: tk.val }}>
        {value}
      </div>
      {subtitle && <div className="kpi__d">{subtitle}</div>}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={t("dossiers.kpiCardToggleTitle")}
        className="kpi"
        style={{ cursor: "pointer", textAlign: "left", font: "inherit", ...activeBorder }}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="kpi" style={activeBorder}>
      {inner}
    </div>
  );
}
